/**
 * Initial Data Factory Functions
 *
 * Shared utility functions for generating initial row data for various working paper components.
 * Used by both component initialization and AdjustTabsDialog toPaper initialization.
 */

import type {CommonRow} from '@/services/auditService/interface/wp-row';
import {useAdjustDraftStore} from '@/store/audit';
import {
    buildDueFromToDirDetailForToPaper,
    dueFromToDirDetailRequiresIssuedCapital,
    isDueFromToDirDirectorRow,
    isDueToDirectorsCode,
    isTurnoverIncomeFsCode,
    normalizeDueFromToDirDetail,
    normalizeDueFromToDirRow,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {
    formatBankCashRowSelectionLabel,
    isFlatEquityAdjustmentCode,
    scanAllRows,
    type AdjustedRowInfo,
} from '@/pages/components/audit/adjustment/adjust/utils/classifyRows';
import {
    createPlFcLiAdjustmentDetail,
    ensurePlFcLiLoanFieldAdjustmentDetail,
    buildPpeSlimFieldAdjDetail,
    isPpeDepnNbvCrossFieldDetail,
    getPpeDepnNbvCrossFieldKey,
    resolvePpeDepnNbvOpposite,
    collectNestedPpeFieldAdjScalars,
    PPE_FIELD_ADJ_KEYS,
    isIaAmorNbvCrossFieldDetail,
    getIaAmorNbvCrossFieldKey,
    resolveIaAmorNbvOpposite,
    buildIaSlimFieldAdjDetail,
    isBlankWpDetail,
} from '@/pages/components/audit/adjustment/adjust/utils/consolidateWpAdjustments';
import {
    computeRentalMonthAmount,
    hasRentalExpenseData,
    normalizeRentFreePeriods,
} from '@/pages/components/audit/adjustment/adjust/dialogs/expenses/sub/rental-utils';
import {formatMonYY, monthsBetweenInclusive, parseYMD} from '@/utils/dataUtils';

const PL_AE_MPF_CODE = 'PL-AE-MPF';
const PL_AE_REN_CODE = 'PL-AE-REN';

/** PL-AE-REN 二级菜单 discriminator（写入 wpFetchCodes，实际仍拉取 PL-AE-REN） */
export type PlAeRenSubmenuFetchCode = 'rental' | 'other';

export function isPlAeRenCode(code?: string | null): boolean {
    return String(code || '').trim().toUpperCase() === PL_AE_REN_CODE;
}

export function isPlAeRenSubmenuFetchCode(wpFetchCode?: string | null): boolean {
    const fetch = String(wpFetchCode || '').trim().toLowerCase();
    return fetch === 'rental' || fetch === 'other';
}

export function resolvePlAeRenSubmenuFetchCode(wpFetchCode?: string | null): PlAeRenSubmenuFetchCode | null {
    const fetch = String(wpFetchCode || '').trim().toLowerCase();
    if (fetch === 'rental' || fetch === 'other') return fetch;
    return null;
}

export type PlAeMpfDetailArrayName = 'salary' | 'wages' | 'directorSalary';

export function isPlAeMpfCode(code?: string | null): boolean {
    return String(code || '').trim().toUpperCase() === PL_AE_MPF_CODE;
}

/** PL-AE-MPF 二级菜单 wpFetchCode → details 数组名（与 SalariesWageWp / DirectorSalaryWp 提交结构一致） */
export function resolvePlAeMpfDetailArrayName(wpFetchCode?: string | null): PlAeMpfDetailArrayName {
    const fetch = String(wpFetchCode || '').trim().toUpperCase();
    if (fetch === 'PL-AE-WAG') return 'wages';
    if (fetch === 'PL-AE-DR') return 'directorSalary';
    return 'salary';
}

/**
 * 将拉取到的 salary/director WP detail 转为 PL-AE-MPF 提交结构：
 * - PL-AE-SAL → { salary: [...] }
 * - PL-AE-WAG → { wages: [...] }（WP 本体仍为 salary[]，提交 MPF 时改 key）
 * - PL-AE-DR → { directorSalary, quarter }
 */
export function normalizeFetchedDetailForPlAeMpf(
    detail: any,
    wpFetchCode?: string | null,
): Record<string, any> {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return createPlAeMpfAdjustmentDetail(0, {wpFetchCode});
    }

    const arrayName = resolvePlAeMpfDetailArrayName(wpFetchCode);
    const metadata: Record<string, any> = {};
    for (const key of WP_METADATA_ONLY_KEYS) {
        if (key in detail) metadata[key] = detail[key];
    }

    if (arrayName === 'directorSalary') {
        return {
            ...metadata,
            directorSalary: Array.isArray(detail.directorSalary) ? detail.directorSalary : [],
            quarter: Array.isArray(detail.quarter) ? detail.quarter : [],
        };
    }

    // Wages WP GET 仍为 salary[]；MPF line details 使用 wages key
    const sourceRows = Array.isArray(detail[arrayName])
        ? detail[arrayName]
        : (Array.isArray(detail.salary) ? detail.salary : []);
    return {
        ...metadata,
        [arrayName]: sourceRows,
    };
}

/** PL-AE-MPF 空数据时的初始 details（adjustment 写在 mpf.adjustment） */
export function createPlAeMpfAdjustmentDetail(
    adjustmentAmount: number,
    options?: {wpFetchCode?: string | null; perClient?: number; name?: string},
): Record<string, any> {
    const arrayName = resolvePlAeMpfDetailArrayName(options?.wpFetchCode);
    const perClient = Number(options?.perClient) || 0;
    const name = options?.name || '';
    const mpf = {
        id: -1,
        mpf: 0,
        adjustment: adjustmentAmount,
        lastYearFinal: 0,
    };

    if (arrayName === 'directorSalary') {
        return {
            directorSalary: [{
                id: 1,
                directorId: null,
                name,
                salary: perClient,
                adjustment: 0,
                lastYearFinal: 0,
                mpf,
            }],
            quarter: [],
        };
    }

    return {
        [arrayName]: [{
            id: 1,
            rowKey: '',
            name,
            salary: perClient,
            adjustment: 0,
            lastYearFinal: 0,
            mpf,
        }],
    };
}

/** 将 adjustment 写入指定行的 mpf.adjustment，并清零其余行的 salary/mpf adjustment */
export function updateMpfRowAdjustment(
    detail: any,
    targetArrayName: string,
    targetRowIndex: number,
    adjustmentAmount: number,
): any {
    if (!detail || typeof detail !== 'object') return detail;
    const result: any = {};

    for (const [key, value] of Object.entries(detail)) {
        if (WP_METADATA_ONLY_KEYS.has(key)) {
            result[key] = value;
            continue;
        }
        if (!Array.isArray(value)) {
            result[key] = value;
            continue;
        }
        result[key] = value.map((row: any, idx: number) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned: any = {...row};
            delete cleaned.adjustments;
            delete cleaned.mpfAdjustments;
            // MPF line：salary/director 行自身 adjustment 保持 0
            cleaned.adjustment = 0;
            if ('mpfAdjustment' in row) cleaned.mpfAdjustment = 0;

            const existingMpf = (row.mpf && typeof row.mpf === 'object') ? row.mpf : {};
            const {adjustments: _mpfAdjList, ...mpfRest} = existingMpf as Record<string, any>;
            if (key === targetArrayName && idx === targetRowIndex) {
                cleaned.mpf = {
                    ...mpfRest,
                    id: Number(mpfRest.id) || -1,
                    mpf: Number(mpfRest.mpf) || 0,
                    lastYearFinal: Number(mpfRest.lastYearFinal) || 0,
                    adjustment: adjustmentAmount,
                };
            } else if (row.mpf && typeof row.mpf === 'object') {
                cleaned.mpf = {
                    ...mpfRest,
                    adjustment: 0,
                };
            }
            return cleaned;
        });
    }
    return result;
}

/** PL-AE-MPF 二级菜单源 WP 为空时的确认跳转文案与目标 code */
export function resolvePlAeMpfSourceEmptyConfirm(
    wpFetchCode?: string | null,
): {message: string; redirectCode: string} | null {
    const fetch = String(wpFetchCode || '').trim().toUpperCase();
    if (fetch === 'PL-AE-DR') {
        return {
            message: "Can not found any director's remuneration, do you fill in director's record first?",
            redirectCode: 'BS-EQ-IC',
        };
    }
    if (fetch === 'PL-AE-WAG') {
        return {
            message: 'Can not found any wages expense, do you fill in wages expense first?',
            redirectCode: 'PL-AE-WAG',
        };
    }
    if (fetch === 'PL-AE-SAL') {
        return {
            message: 'Can not found any salary allowance, do you fill in salary allowance first?',
            redirectCode: 'PL-AE-SAL',
        };
    }
    // 无二级菜单 wpFetchCode 时不走空源确认（避免影响非 submenu 路径）
    return null;
}

function buildPlAeMpfSourceEmptyConfirmResult(
    code: string,
    wpFetchCode?: string | null,
): ToPaperWpFetchResult | null {
    const confirm = resolvePlAeMpfSourceEmptyConfirm(wpFetchCode);
    if (!confirm) return null;
    return {
        kind: 'mpfSourceEmptyConfirm',
        code,
        message: confirm.message,
        redirectCode: confirm.redirectCode,
    };
}

function hasPlAeMpfSourceRows(detail: Record<string, any>, arrayName: PlAeMpfDetailArrayName): boolean {
    const rows = Array.isArray(detail[arrayName]) ? detail[arrayName] : [];
    return rows.length > 0;
}

/** 按人员行（directorSalary/salary/wages）组装 rowSelection 选项，adjustment 目标为 mpf */
function buildPlAeMpfPersonSelectionRows(
    detail: Record<string, any>,
    arrayName: PlAeMpfDetailArrayName,
): AdjustedRowInfo[] {
    const rows = Array.isArray(detail[arrayName]) ? detail[arrayName] : [];
    return rows.map((row: any, idx: number) => {
        const name = row?.name || row?.directorName || row?.item || `Row ${idx + 1}`;
        const mpfPerClient = Number(row?.mpf?.mpf) || 0;
        const salaryPerClient = Number(row?.salary ?? row?.perClient) || 0;
        const mpfAdj = Number(row?.mpf?.adjustment) || 0;
        return {
            arrayName,
            rowIndex: idx,
            row,
            adjustment: mpfAdj,
            rowKey: `${name} (MPF)`,
            perClient: mpfPerClient || salaryPerClient,
            subField: 'mpf',
        };
    });
}

function mapToPaperRowsWithSubField(rows: AdjustedRowInfo[]): ToPaperWpRowSelectionRow[] {
    return rows.map((r) => ({
        arrayName: r.arrayName,
        rowIndex: r.rowIndex,
        rowKey: r.rowKey,
        perClient: r.perClient,
        row: r.row,
        ...(r.subField ? {subField: r.subField} : {}),
    }));
}

function resolvePlAeMpfToPaperResult(
    input: ResolveToPaperWpFetchInput,
    detail: any,
): ToPaperWpFetchResult {
    const {code, calcAdj, toLine, perClient} = input;
    const normalized = normalizeFetchedDetailForPlAeMpf(detail, input.wpFetchCode);
    const arrayName = resolvePlAeMpfDetailArrayName(input.wpFetchCode);

    // 源 WP（DR/SAL/WAG）无人员行：有二级菜单 wpFetchCode 时确认跳转
    if (!hasPlAeMpfSourceRows(normalized, arrayName)) {
        const emptyConfirm = buildPlAeMpfSourceEmptyConfirmResult(code, input.wpFetchCode);
        if (emptyConfirm) return emptyConfirm;
        // 无 submenu：保持造空行（不影响非二级菜单路径）
        return {
            kind: 'setDetail',
            code,
            detail: createPlAeMpfAdjustmentDetail(calcAdj, {
                wpFetchCode: input.wpFetchCode,
                perClient,
                name: getToPaperLineName(toLine, code),
            }),
            extraRowKeys: [`${code}:${arrayName}:0:mpf`],
        };
    }

    // Non-legacy: >=1 → rowSelection (+ Empty record). Legacy excluded N/A for MPF.
    const personRows = buildPlAeMpfPersonSelectionRows(normalized, arrayName);

    if (shouldOpenToPaperRowSelection(code, personRows.length)) {
        return {
            kind: 'rowSelection',
            code,
            parDetail: normalized,
            adjustmentAmount: calcAdj,
            rows: withEmptyRecordOption(code, mapToPaperRowsWithSubField(personRows), arrayName),
            waitForUser: true,
        };
    }

    const single = personRows[0];
    return {
        kind: 'setDetail',
        code,
        detail: updateMpfRowAdjustment(
            normalized,
            single.arrayName,
            single.rowIndex,
            calcAdj,
        ),
        extraRowKeys: [`${code}:${single.arrayName}:${single.rowIndex}:mpf`],
    };
}

// ── PL-AE-REN 二级菜单（rental / other）toPaper 解析 ─────────────────────

const PL_AE_REN_SOURCE_EMPTY_MESSAGE =
    'Rental Expenses record is empty, Would you create the record fist?';

type RentalWpRow = {
    clientKey?: string;
    address?: string;
    fromDate?: string | null;
    toDate?: string | null;
    monthlyRent?: string | number | null;
    rentFreePeriods?: any;
    otherInfo?: {
        deletedMonthsByCol?: Record<string, string[]>;
        monthChangesByCol?: Record<string, Record<string, number>>;
        extraExpenseRows?: Array<{
            id?: string;
            item?: string;
            expense?: number;
            adjustment?: number;
        }>;
        [key: string]: any;
    } | null;
    [key: string]: any;
};

function extractRentalRowsFromDetail(detail: any): RentalWpRow[] {
    if (!detail || typeof detail !== 'object') return [];
    if (Array.isArray(detail.rows)) return detail.rows as RentalWpRow[];
    // Some WP payloads nest under rentalExpenses
    if (Array.isArray(detail?.rentalExpenses?.rows)) {
        return detail.rentalExpenses.rows as RentalWpRow[];
    }
    return [];
}

function listVisibleRentalMonthsForRow(
    row: RentalWpRow,
    periodFrom?: string | null,
    periodTo?: string | null,
): string[] {
    const pf = parseYMD(periodFrom);
    const pt = parseYMD(periodTo);
    const rf = parseYMD(row.fromDate);
    const rt = parseYMD(row.toDate);
    if (!pf || !pt || !rf || !rt) return [];
    const effectiveFrom = rf > pf ? rf : pf;
    const effectiveTo = rt < pt ? rt : pt;
    if (effectiveFrom > effectiveTo) return [];
    const clientKey = String(row.clientKey || '');
    const deleted = row.otherInfo?.deletedMonthsByCol?.[clientKey] || [];
    return monthsBetweenInclusive(effectiveFrom, effectiveTo)
        .map(formatMonYY)
        .filter((month) => !deleted.includes(month));
}

function buildPlAeRenMonthSelectionRows(
    detail: any,
    periodFrom?: string | null,
    periodTo?: string | null,
): ToPaperWpRowSelectionRow[] {
    const rows = extractRentalRowsFromDetail(detail);
    const out: ToPaperWpRowSelectionRow[] = [];
    rows.forEach((row, rentalRowIndex) => {
        const clientKey = String(row.clientKey || `rk-${rentalRowIndex}`);
        const labelBase = String(row.address || '').trim() || `Rental #${rentalRowIndex + 1}`;
        const months = listVisibleRentalMonthsForRow(row, periodFrom, periodTo);
        months.forEach((month, monthIdx) => {
            const monthAmount = computeRentalMonthAmount(month, {
                periodFrom: periodFrom ?? null,
                periodTo: periodTo ?? null,
                fromDate: row.fromDate,
                toDate: row.toDate,
                monthlyRent: row.monthlyRent,
                rentFreePeriods: normalizeRentFreePeriods(row.rentFreePeriods),
            });
            out.push({
                arrayName: 'rentalMonths',
                rowIndex: out.length,
                rowKey: `${labelBase} — ${month}`,
                perClient: monthAmount,
                row: {
                    clientKey,
                    month,
                    rentalRowIndex,
                    monthIdx,
                },
                subField: 'rentalMonth',
            });
        });
    });
    return out;
}

type ExtraExpenseScanItem = {
    clientKey: string;
    rentalRowIndex: number;
    extraIndex: number;
    extraId: string;
    item: string;
    expense: number;
    adjustment: number;
    row: any;
};

function scanPlAeRenOtherExpenses(detail: any): ExtraExpenseScanItem[] {
    const rows = extractRentalRowsFromDetail(detail);
    const out: ExtraExpenseScanItem[] = [];
    rows.forEach((row, rentalRowIndex) => {
        const clientKey = String(row.clientKey || `rk-${rentalRowIndex}`);
        const extras = Array.isArray(row.otherInfo?.extraExpenseRows)
            ? row.otherInfo!.extraExpenseRows!
            : [];
        extras.forEach((extra, extraIndex) => {
            const extraId = String(extra?.id || `extra-${rentalRowIndex}-${extraIndex}`);
            out.push({
                clientKey,
                rentalRowIndex,
                extraIndex,
                extraId,
                item: String(extra?.item || '').trim() || `Other #${out.length + 1}`,
                expense: Number(extra?.expense) || 0,
                adjustment: Number(extra?.adjustment) || 0,
                row: extra,
            });
        });
    });
    return out;
}

function buildPlAeRenOtherSelectionRows(detail: any): ToPaperWpRowSelectionRow[] {
    return scanPlAeRenOtherExpenses(detail).map((item, idx) => ({
        arrayName: 'extraExpenseRows',
        rowIndex: idx,
        rowKey: item.item,
        perClient: item.expense,
        row: {
            clientKey: item.clientKey,
            extraId: item.extraId,
            rentalRowIndex: item.rentalRowIndex,
            extraIndex: item.extraIndex,
        },
        subField: 'otherExpense',
    }));
}

function cloneRentalDetailForToPaper(detail: any): any {
    if (!detail || typeof detail !== 'object') {
        return {rows: [], adjustment: 0};
    }
    // Shallow-clone rows / otherInfo so we do not mutate cached WP detail
    const rows = extractRentalRowsFromDetail(detail).map((row) => ({
        ...row,
        otherInfo: row.otherInfo
            ? {
                ...row.otherInfo,
                extraExpenseRows: Array.isArray(row.otherInfo.extraExpenseRows)
                    ? row.otherInfo.extraExpenseRows.map((e) => ({...e}))
                    : row.otherInfo.extraExpenseRows,
                monthChangesByCol: row.otherInfo.monthChangesByCol
                    ? {...row.otherInfo.monthChangesByCol}
                    : row.otherInfo.monthChangesByCol,
            }
            : row.otherInfo,
    }));
    const {adjustments: _adj, ...rest} = detail as Record<string, any>;
    return {
        ...rest,
        rows,
    };
}

function genExtraExpenseId(): string {
    return `extra-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Apply rental-month or other-expense selection onto PL-AE-REN toPaper detail. */
export function applyPlAeRenRowSelection(
    detail: any,
    selected: ToPaperWpRowSelectionRow,
    adjustmentAmount: number,
): any {
    const next = cloneRentalDetailForToPaper(detail);
    const rows: RentalWpRow[] = Array.isArray(next.rows) ? next.rows : [];
    const adj = Number(adjustmentAmount) || 0;
    const sub = String(selected.subField || '').trim();
    const meta = selected.row || {};

    if (sub === 'rentalMonth') {
        const clientKey = String(meta.clientKey || '');
        const month = String(meta.month || '');
        const rentalRowIndex = Number(meta.rentalRowIndex);
        const target = Number.isFinite(rentalRowIndex) && rentalRowIndex >= 0
            ? rows[rentalRowIndex]
            : rows.find((r) => String(r.clientKey || '') === clientKey);
        if (target && month) {
            const key = String(target.clientKey || clientKey);
            const otherInfo = {...(target.otherInfo || {})};
            const monthChangesByCol = {...(otherInfo.monthChangesByCol || {})};
            const forCol = {...(monthChangesByCol[key] || {})};
            forCol[month] = (Number(forCol[month]) || 0) + adj;
            if (Math.abs(forCol[month]) < 0.000001) delete forCol[month];
            if (Object.keys(forCol).length > 0) monthChangesByCol[key] = forCol;
            else delete monthChangesByCol[key];
            otherInfo.monthChangesByCol = monthChangesByCol;
            target.otherInfo = otherInfo;
        }
        next.adjustment = adj;
        next.rentalAdjustment = adj;
        next.otherExpenseAdjustment = 0;
        next.rentalMonthAdjustments = month ? {[month]: adj} : {};
        next.otherExpenseAdjustmentsById = {};
        return next;
    }

    if (sub === 'otherExpense') {
        const clientKey = String(meta.clientKey || '');
        const extraId = String(meta.extraId || '');
        const rentalRowIndex = Number(meta.rentalRowIndex);
        const extraIndex = Number(meta.extraIndex);
        const target = Number.isFinite(rentalRowIndex) && rentalRowIndex >= 0
            ? rows[rentalRowIndex]
            : rows.find((r) => String(r.clientKey || '') === clientKey);
        if (target) {
            const otherInfo = {...(target.otherInfo || {})};
            const extras = Array.isArray(otherInfo.extraExpenseRows)
                ? otherInfo.extraExpenseRows.map((e: any) => ({...e}))
                : [];
            let hit = Number.isFinite(extraIndex) && extraIndex >= 0 ? extras[extraIndex] : null;
            if (!hit && extraId) {
                hit = extras.find((e: any) => String(e?.id || '') === extraId) || null;
            }
            if (hit) {
                hit.adjustment = (Number(hit.adjustment) || 0) + adj;
            }
            otherInfo.extraExpenseRows = extras;
            target.otherInfo = otherInfo;
        }
        next.adjustment = adj;
        next.rentalAdjustment = 0;
        next.otherExpenseAdjustment = adj;
        next.rentalMonthAdjustments = {};
        next.otherExpenseAdjustmentsById = extraId ? {[extraId]: adj} : {};
        return next;
    }

    // Fallback: top-level only
    next.adjustment = adj;
    return next;
}

/** Create a new other-expense row on the first rental record and bind adjustment. */
export function applyPlAeRenNewOtherExpense(detail: any, adjustmentAmount: number): {
    detail: any;
    extraRowKey: string;
} {
    const next = cloneRentalDetailForToPaper(detail);
    const rows: RentalWpRow[] = Array.isArray(next.rows) ? next.rows : [];
    const adj = Number(adjustmentAmount) || 0;
    const target = rows[0];
    const extraId = genExtraExpenseId();
    if (target) {
        const otherInfo = {...(target.otherInfo || {})};
        const extras = Array.isArray(otherInfo.extraExpenseRows)
            ? otherInfo.extraExpenseRows.map((e: any) => ({...e}))
            : [];
        extras.push({
            id: extraId,
            item: '',
            expense: 0,
            adjustment: adj,
        });
        otherInfo.extraExpenseRows = extras;
        target.otherInfo = otherInfo;
    }
    next.adjustment = adj;
    next.rentalAdjustment = 0;
    next.otherExpenseAdjustment = adj;
    next.rentalMonthAdjustments = {};
    next.otherExpenseAdjustmentsById = {[extraId]: adj};
    return {
        detail: next,
        extraRowKey: `${PL_AE_REN_CODE}:extraExpenseRows:0:otherExpense`,
    };
}

function buildPlAeRenSourceEmptyConfirm(code: string): ToPaperWpFetchResult {
    return {
        kind: 'mpfSourceEmptyConfirm',
        code,
        message: PL_AE_REN_SOURCE_EMPTY_MESSAGE,
        redirectCode: PL_AE_REN_CODE,
    };
}

/**
 * PL-AE-REN 二级菜单 toPaper 结果：
 * - openingAmount === 0 → WP→FS 形态（无 WP detail）
 * - rental：有月租金 → rowSelection；无基础数据 → 空源确认
 * - other：有 other expenses → 1 条直绑 / ≥2 选行；有 rental 无 other → 新建；无 rental → 空源确认
 */
function resolvePlAeRenToPaperResult(
    input: ResolveToPaperWpFetchInput,
    detail: any,
): ToPaperWpFetchResult {
    const {code, calcAdj, toLine} = input;
    const submenu = resolvePlAeRenSubmenuFetchCode(input.wpFetchCode);
    if (!submenu) {
        // 无二级菜单：保持原 field WP→WP 空壳
        return {
            kind: 'setDetail',
            code,
            detail: createInitialDetailForCode(code, {...toLine, amount: input.perClient}, calcAdj, {
                wpFetchCode: input.wpFetchCode,
            }),
            extraRowKeys: [`${code}:_synthetic:0`],
        };
    }

    const openingAmount = Number(toLine?.openingAmount) || 0;
    // openingAmount == 0：按 WP→FS 形态（details 为空）
    if (Math.abs(openingAmount) < 0.000001) {
        return {kind: 'none'};
    }

    const rows = extractRentalRowsFromDetail(detail);
    const hasRental = hasRentalExpenseData(rows as any);

    if (submenu === 'rental') {
        if (!hasRental) {
            return buildPlAeRenSourceEmptyConfirm(code);
        }
        const monthRows = buildPlAeRenMonthSelectionRows(
            detail,
            input.periodFrom,
            input.periodTo,
        );
        if (monthRows.length === 0) {
            return buildPlAeRenSourceEmptyConfirm(code);
        }
        return {
            kind: 'rowSelection',
            code,
            parDetail: cloneRentalDetailForToPaper(detail),
            adjustmentAmount: calcAdj,
            rows: withEmptyRecordOption(code, monthRows, 'rows'),
            waitForUser: true,
        };
    }

    // submenu === 'other'
    if (!hasRental) {
        return buildPlAeRenSourceEmptyConfirm(code);
    }

    const otherRows = buildPlAeRenOtherSelectionRows(detail);
    if (shouldOpenToPaperRowSelection(code, otherRows.length)) {
        return {
            kind: 'rowSelection',
            code,
            parDetail: cloneRentalDetailForToPaper(detail),
            adjustmentAmount: calcAdj,
            rows: withEmptyRecordOption(code, otherRows, 'extraExpenseRows'),
            waitForUser: true,
        };
    }
    if (otherRows.length === 1) {
        const single = otherRows[0];
        return {
            kind: 'setDetail',
            code,
            detail: applyPlAeRenRowSelection(
                cloneRentalDetailForToPaper(detail),
                single,
                calcAdj,
            ),
            extraRowKeys: [`${code}:${single.arrayName}:${single.rowIndex}:otherExpense`],
        };
    }

    // 有 rental 基础数据但无 other expenses → 新建一条
    const created = applyPlAeRenNewOtherExpense(detail, calcAdj);
    return {
        kind: 'setDetail',
        code,
        detail: created.detail,
        extraRowKeys: [created.extraRowKey],
    };
}

// ── BS-NCA-PPE / PL-AE-DEP 分类（xxxRow）toPaper 解析 ───────────────────

const PPE_WP_CODE = 'BS-NCA-PPE';
const PL_AE_DEP_CODE = 'PL-AE-DEP';

const PPE_CATEGORY_ROW_KEY_SET = new Set([
    'locationOfPropertyRow',
    'commercialAllowanceRow',
    'industrialAllowanceRow',
    'furnitureFixtureRow',
    'computerHardSoftRow',
    'fullPayMotorRow',
    'hirePurchaseMotorRow',
    'machineryRow',
    'liftEscalatorRow',
    'equipmentRow',
]);

export function isPpeOrDepToPaperCode(code?: string | null): boolean {
    const upper = String(code || '').trim().toUpperCase();
    return upper === PPE_WP_CODE || upper === PL_AE_DEP_CODE;
}

/**
 * FsSelectionDrawer 写入的 PPE 分类 discriminator（非真实 WP code）。
 * 兼容 store normalizeLineCode 后的全大写（hirePurchaseMotorRow → HIREPURCHASEMOTORROW）。
 */
export function resolvePpeCategoryFetchCode(wpFetchCode?: string | null): string | null {
    const key = String(wpFetchCode || '').trim();
    if (!key) return null;
    if (PPE_CATEGORY_ROW_KEY_SET.has(key)) return key;
    const lower = key.toLowerCase();
    for (const canonical of PPE_CATEGORY_ROW_KEY_SET) {
        if (canonical.toLowerCase() === lower) return canonical;
    }
    return null;
}

export function isPpeCategoryFetchCode(wpFetchCode?: string | null): boolean {
    return resolvePpeCategoryFetchCode(wpFetchCode) != null;
}

/**
 * 其它 paper-registry 组件 → PropertyPlantEquipment（BS-NCA-PPE）根节点（无分类 wpFetchCode）。
 * 同 Grand Total NBV：仅 line-level adjustLine（`details: []`），不要求 unified WP detail。
 * 仅匹配 to=BS-NCA-PPE；不影响 PPE/DEP 自身、分类 xxxRow、IA/ITL 既有路径。
 */
export function isInboundPpeRootLineOnlyToPaper(
    toCode?: string | null,
    fromCode?: string | null,
    wpFetchCode?: string | null,
): boolean {
    const toUpper = String(toCode || '').trim().toUpperCase();
    if (toUpper !== PPE_WP_CODE) return false;
    if (isPpeCategoryFetchCode(wpFetchCode)) return false;
    const fromUpper = String(fromCode || '').trim().toUpperCase();
    if (fromUpper === PPE_WP_CODE || fromUpper === PL_AE_DEP_CODE) return false;
    return true;
}

/**
 * 其它组件 → BS-NCA-PPE 根节点：line-only 空 detail（与 Grand Total NBV 一致）。
 * 仅由 adjustDraftStore.fetchAndAssembleToPaper 短路调用（带 fromCode）；
 * 不接入 shared resolveToPaperWpFetchResult*，避免影响其它 PPE 路径。
 */
export function tryBuildInboundPpeRootToPaperResult(opts: {
    toCode: string;
    fromCode?: string | null;
    wpFetchCode?: string | null;
}): ToPaperWpFetchResult | null {
    if (!isInboundPpeRootLineOnlyToPaper(opts.toCode, opts.fromCode, opts.wpFetchCode)) {
        return null;
    }
    const toCode = String(opts.toCode || '').trim().toUpperCase();
    return {
        kind: 'setDetail',
        code: toCode,
        detail: {},
        extraRowKeys: [`${toCode}:_synthetic:0`],
    };
}

/**
 * PL-AE-REN without rental|other submenu (e.g. DTD → REN):
 * - Has saved WP rows → clone real detail + top-level `adjustment` for AdjustTabs `_synthetic`
 *   (must NOT invent empty `{rows:[blank]}` shell — that overwrites persisted rental WP on submit)
 * - No rental data → empty detail (same as legacy NO_WP `details: []`, line amount only)
 */
export function tryBuildPlainRenFieldToPaperResult(opts: {
    toCode: string;
    calcAdj: number;
    wpFetchCode?: string | null;
    toLine?: {name?: string | null; scheduleName?: string | null; openingAmount?: number | null; amount?: number | null} | null;
    perClient?: number;
    /** Unwrapped PL-AE-REN GET detail (caller fetches). */
    wpDetail?: any;
}): ToPaperWpFetchResult | null {
    const toCode = String(opts.toCode || '').trim().toUpperCase();
    if (!isPlAeRenCode(toCode)) return null;
    if (isPlAeRenSubmenuFetchCode(opts.wpFetchCode)) return null;

    const cloned = cloneRentalDetailForToPaper(opts.wpDetail || {});
    const rows = extractRentalRowsFromDetail(cloned);
    if (hasRentalExpenseData(rows as any)) {
        return {
            kind: 'setDetail',
            code: toCode,
            detail: {
                ...cloned,
                adjustment: opts.calcAdj,
            },
            extraRowKeys: [`${toCode}:_synthetic:0`],
        };
    }

    // Empty WP: line-only (do not invent fake rows that persist and wipe schedule)
    return {
        kind: 'setDetail',
        code: toCode,
        detail: {},
        extraRowKeys: [`${toCode}:_synthetic:0`],
    };
}

/**
 * Depn↔NBV cross-field WP→WP: select opposite PPE/DEP leaf (no category wpFetchCode)
 * → slim `{ppeRowId, nbvCfAdj|depnChgAdj}` toPaper without category List / full WP shell.
 *
 * Pure helper — call only from adjustDraftStore assembly (explicit fromDetail).
 * Not wired into shared resolveToPaperWpFetchResult* so other toPaper paths are unchanged.
 */
export function tryBuildPpeDepnNbvCrossToPaperResult(opts: {
    toCode: string;
    calcAdj: number;
    wpFetchCode?: string | null;
    fromCode?: string | null;
    fromDetail?: any;
    /** Session-scoped: PPE Depn chg skip-Drawer path only. */
    allowBlankDepToPpe?: boolean;
}): ToPaperWpFetchResult | null {
    const toCode = String(opts.toCode || '').trim().toUpperCase();
    if (!isPpeOrDepToPaperCode(toCode)) return null;
    if (isPpeCategoryFetchCode(opts.wpFetchCode)) return null;

    const fromUpper = String(opts.fromCode || '').trim().toUpperCase();

    // Depn chg skip-Drawer: DEP→PPE stays line-only even when from has row depnChgAdj.
    if (opts.allowBlankDepToPpe && fromUpper === 'PL-AE-DEP' && toCode === 'BS-NCA-PPE') {
        return {
            kind: 'setDetail',
            code: toCode,
            detail: {},
            extraRowKeys: [`${toCode}:_synthetic:0`],
        };
    }

    // Narrow: only Grand Total NBV (BS-NCA-PPE + blank details) → DEP with details: [].
    // Do not treat arbitrary blank DEP↔PPE as schedule-level (preserves other empty-detail paths).
    if (isBlankWpDetail(opts.fromDetail) && fromUpper === 'BS-NCA-PPE' && toCode === 'PL-AE-DEP') {
        return {
            kind: 'setDetail',
            code: toCode,
            detail: {},
            extraRowKeys: [`${toCode}:_synthetic:0`],
        };
    }

    if (!isPpeDepnNbvCrossFieldDetail(opts.fromDetail, opts.fromCode)) return null;

    const fromKey = getPpeDepnNbvCrossFieldKey(opts.fromDetail, opts.fromCode);
    const opposite = resolvePpeDepnNbvOpposite(opts.fromCode, fromKey);
    if (!opposite || opposite.toCode !== toCode) return null;

    const fromDetail = opts.fromDetail && typeof opts.fromDetail === 'object'
        ? opts.fromDetail as Record<string, any>
        : {};
    const nested = collectNestedPpeFieldAdjScalars(fromDetail);
    let ppeRowId = Number(fromDetail.ppeRowId) > 0
        ? Number(fromDetail.ppeRowId)
        : nested.ppeRowId;
    if (!(ppeRowId != null && ppeRowId > 0)) {
        // Fallback: any positive id on a row that carried the from field key
        for (const rowKey of Object.keys(fromDetail)) {
            if (!String(rowKey).endsWith('Row') && rowKey !== 'testRow') continue;
            const rows = fromDetail[rowKey];
            if (!Array.isArray(rows)) continue;
            for (const row of rows) {
                if (!row || typeof row !== 'object') continue;
                const hasField = fromKey
                    ? Math.abs(Number((row as any)[fromKey]) || 0) >= 0.001
                    : PPE_FIELD_ADJ_KEYS.some((k) => Math.abs(Number((row as any)[k]) || 0) >= 0.001);
                if (!hasField) continue;
                const rid = Number((row as any).id);
                if (Number.isFinite(rid) && rid > 0) {
                    ppeRowId = rid;
                    break;
                }
            }
            if (ppeRowId != null && ppeRowId > 0) break;
        }
    }

    return {
        kind: 'setDetail',
        code: toCode,
        detail: buildPpeSlimFieldAdjDetail({
            ppeRowId,
            fieldKey: opposite.toFieldKey,
            amount: opts.calcAdj,
        }),
        extraRowKeys: [`${toCode}:_synthetic:0`],
    };
}

/**
 * Amor↔NBV cross-field WP→WP: select opposite IA/AIA leaf
 * → slim `{intangibleRowId, nbvCfAdj|amorChgAdj}` toPaper without full WP shell / rowSelection.
 *
 * Pure helper — call only from adjustDraftStore assembly (explicit fromDetail).
 * Not wired into shared resolveToPaperWpFetchResult*.
 */
export function tryBuildIaAmorNbvCrossToPaperResult(opts: {
    toCode: string;
    calcAdj: number;
    fromCode?: string | null;
    fromDetail?: any;
    /** Session-scoped: IA Amor chg skip-Drawer path only. */
    allowBlankAiaToIa?: boolean;
    /** Session-scoped: Taxi Depn chg skip-Drawer path only (DEP→ITL). */
    allowBlankDepToItl?: boolean;
}): ToPaperWpFetchResult | null {
    const toCode = String(opts.toCode || '').trim().toUpperCase();
    if (
        toCode !== 'BS-NCA-IA'
        && toCode !== 'PL-AE-AIA'
        && toCode !== 'BS-NCA-ITL'
        && toCode !== 'PL-AE-DEP'
    ) return null;

    const fromUpper = String(opts.fromCode || '').trim().toUpperCase();

    // Amor chg skip-Drawer: AIA→IA stays line-only even when from has row amorChgAdj.
    if (opts.allowBlankAiaToIa && fromUpper === 'PL-AE-AIA' && toCode === 'BS-NCA-IA') {
        return {
            kind: 'setDetail',
            code: toCode,
            detail: {},
            extraRowKeys: [`${toCode}:_synthetic:0`],
        };
    }

    // Taxi Depn skip-Drawer: DEP→ITL stays line-only (PPE Depn parity; session flag avoids PPE clash).
    if (opts.allowBlankDepToItl && fromUpper === 'PL-AE-DEP' && toCode === 'BS-NCA-ITL') {
        return {
            kind: 'setDetail',
            code: toCode,
            detail: {},
            extraRowKeys: [`${toCode}:_synthetic:0`],
        };
    }

    // Schedule-level empty details → opposite with details: [].
    // Default: only Total NBV outbound (IA→AIA / ITL→DEP). Do not hijack blank AIA/DEP.
    if (isBlankWpDetail(opts.fromDetail)) {
        if (
            (fromUpper === 'BS-NCA-IA' && toCode === 'PL-AE-AIA')
            || (fromUpper === 'BS-NCA-ITL' && toCode === 'PL-AE-DEP')
        ) {
            return {
                kind: 'setDetail',
                code: toCode,
                detail: {},
                extraRowKeys: [`${toCode}:_synthetic:0`],
            };
        }
    }

    if (!isIaAmorNbvCrossFieldDetail(opts.fromDetail, opts.fromCode)) return null;

    const fromKey = getIaAmorNbvCrossFieldKey(opts.fromDetail, opts.fromCode);
    const opposite = resolveIaAmorNbvOpposite(opts.fromCode, fromKey);
    if (!opposite || opposite.toCode !== toCode) return null;

    const fromDetail = opts.fromDetail && typeof opts.fromDetail === 'object'
        ? opts.fromDetail as Record<string, any>
        : {};
    const intangibleRowId = Number(fromDetail.intangibleRowId) > 0
        ? Number(fromDetail.intangibleRowId)
        : undefined;
    if (!(intangibleRowId != null && intangibleRowId > 0)) return null;

    return {
        kind: 'setDetail',
        code: toCode,
        detail: buildIaSlimFieldAdjDetail({
            intangibleRowId,
            fieldKey: opposite.toFieldKey,
            amount: opts.calcAdj,
        }),
        extraRowKeys: [`${toCode}:_synthetic:0`],
    };
}

/** 浅拷贝 PPE WP detail，避免污染 fsAdjustStore.wpDetailsCache（对齐 cloneRentalDetailForToPaper） */
function clonePpeDetailForToPaper(detail: any): any {
    if (!detail || typeof detail !== 'object') return {};
    const next: Record<string, any> = {...detail};
    for (const arrayName of PPE_CATEGORY_ROW_KEY_SET) {
        const rows = next[arrayName];
        next[arrayName] = Array.isArray(rows)
            ? rows.map((row) => (row && typeof row === 'object' ? {...row} : row))
            : [];
    }
    return next;
}

function isPpeSelectableDataRow(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    // 仅有 id 的空白 shell（无 item）不算可选数据行 — 走空分类 nbvCfAdj/depnChgAdj 路径
    return String(row.item || '').trim().length > 0;
}

function buildPpeCategorySelectionRows(
    detail: any,
    arrayName: string,
): ToPaperWpRowSelectionRow[] {
    const rows = Array.isArray(detail?.[arrayName]) ? detail[arrayName] : [];
    const result: ToPaperWpRowSelectionRow[] = [];
    rows.forEach((row: any, rowIndex: number) => {
        if (!isPpeSelectableDataRow(row)) return;
        result.push({
            arrayName,
            rowIndex,
            rowKey: String(row.item || '').trim() || `Row ${rowIndex + 1}`,
            perClient: Number(row.nbvCf ?? row.costBf ?? row.perClient) || 0,
            row,
        });
    });
    return result;
}

/** BS-NCA-PPE → nbvCfAdj；PL-AE-DEP → depnChgAdj（仅空分类 toPaper shell） */
function resolvePpeToPaperFieldKey(code?: string | null): 'nbvCfAdj' | 'depnChgAdj' {
    const upper = String(code || '').trim().toUpperCase();
    return upper === PL_AE_DEP_CODE ? 'depnChgAdj' : 'nbvCfAdj';
}

/**
 * 有数据分类 toPaper：仅写入所选行的 row.adjustment（完整 WP 壳，不 omit 空 xxxRow）。
 * 与公共 processPpeCategoryAdjustment / AdjustTabs 分类路径一致。
 * 空分类 field 路径见 resolvePpeToPaperResult → createEmptyPpeCategoryRow。
 */
export function applyPpeToPaperRowDetail(
    detail: any,
    arrayName: string,
    rowIndex: number,
    adjustmentAmount: number,
): any {
    const base = detail && typeof detail === 'object' ? {...detail} : {};
    const existing = Array.isArray(base[arrayName]) ? base[arrayName] : [];
    base[arrayName] = existing.map((row: any, idx: number) => {
        if (!row || typeof row !== 'object') return row;
        const cleaned: any = {...row};
        delete cleaned.adjustments;
        cleaned.adjustment = idx === rowIndex ? adjustmentAmount : 0;
        return cleaned;
    });
    return base;
}

/**
 * 空分类专用：按 PPERow 结构建空对象，写入 nbvCfAdj / depnChgAdj（不写 row.adjustment）。
 * 仅 resolvePpeToPaperResult 无数据分支调用，不影响有数据分类 / 公共组件。
 */
function createEmptyPpeCategoryRow(
    adjustmentAmount: number,
    fieldKey: 'nbvCfAdj' | 'depnChgAdj',
): any {
    const row: Record<string, any> = {
        date: null,
        item: '',
        costBf: null,
        additions: null,
        disposalCost: null,
        accDepBf: null,
    };
    if (Math.abs(Number(adjustmentAmount) || 0) >= 0.001) {
        row[fieldKey] = adjustmentAmount;
    }
    return row;
}

/** 仅空分类 toPaper setDetail：省略空 xxxRow / testRow。 */
function omitEmptyPpeCategoryRowKeys<T extends Record<string, any>>(detail: T): T {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return detail;
    const next: Record<string, any> = {...detail};
    for (const rowKey of PPE_CATEGORY_ROW_KEY_SET) {
        const rows = next[rowKey];
        if (Array.isArray(rows) && rows.length === 0) {
            delete next[rowKey];
        }
    }
    if (Array.isArray(next.testRow) && next.testRow.length === 0) {
        delete next.testRow;
    }
    return next as T;
}

/**
 * PPE / Depreciation 分类 toPaper：
 * - non-legacy: >=1 行 → rowSelection（末 Empty record）
 * - legacy: ≥2 行 → rowSelection；1 行直绑
 * - 0 行 → 直接 setDetail（空分类新建一行）
 * details 经 clone 后写入 adjustDraftStore（与 PL-AE-REN 一致，不污染 WP cache）
 */
function resolvePpeToPaperResult(
    input: ResolveToPaperWpFetchInput,
    detail: any,
): ToPaperWpFetchResult {
    const {code, calcAdj} = input;
    const arrayName = resolvePpeCategoryFetchCode(input.wpFetchCode);
    if (!arrayName) {
        // Original: no category discriminator → clone full WP detail (unchanged for non-NBV paths).
        // Schedule-level NBV opposite uses tryBuildPpeDepnNbvCrossToPaperResult (empty detail), not this branch.
        return {
            kind: 'setDetail',
            code,
            detail: clonePpeDetailForToPaper(detail),
            extraRowKeys: [`${code}:_synthetic:0`],
        };
    }

    const parDetail = clonePpeDetailForToPaper(detail);
    if (!Array.isArray(parDetail[arrayName])) {
        parDetail[arrayName] = [];
    }

    const selectionRows = buildPpeCategorySelectionRows(parDetail, arrayName);

    if (shouldOpenToPaperRowSelection(code, selectionRows.length)) {
        return {
            kind: 'rowSelection',
            code,
            parDetail,
            adjustmentAmount: calcAdj,
            rows: withEmptyRecordOption(code, selectionRows, arrayName),
            waitForUser: true,
        };
    }

    if (selectionRows.length === 1) {
        const single = selectionRows[0];
        return {
            kind: 'setDetail',
            code,
            detail: applyPpeToPaperRowDetail(parDetail, arrayName, single.rowIndex, calcAdj),
            extraRowKeys: [`${code}:${arrayName}:${single.rowIndex}`],
        };
    }

    // 仅无数据分支：空 PPERow + nbvCfAdj/depnChgAdj，并省略其它空 xxxRow（不影响有数据分类路径）
    const fieldKey = resolvePpeToPaperFieldKey(code);
    const emptyRow = createEmptyPpeCategoryRow(calcAdj, fieldKey);
    const withEmpty = omitEmptyPpeCategoryRowKeys({
        ...parDetail,
        [arrayName]: [emptyRow],
    });
    return {
        kind: 'setDetail',
        code,
        detail: withEmpty,
        extraRowKeys: [`${code}:${arrayName}:0`],
    };
}

// ── Intangible Assets / Amortisation toPaper（BS-NCA-IA / PL-AE-AIA）────────
// BS-NCA-ITL also uses these helpers (isIaOrAiaToPaperCode); IA/AIA branches unchanged.
const IA_WP_CODE = 'BS-NCA-IA';
const PL_AE_AIA_CODE = 'PL-AE-AIA';
const IA_INTANGIBLE_ROWS_KEY = 'intangibleRows';

export function isIaOrAiaToPaperCode(code?: string | null): boolean {
    const upper = String(code || '').trim().toUpperCase();
    return upper === IA_WP_CODE || upper === PL_AE_AIA_CODE || upper === 'BS-NCA-ITL';
}

/** PL-AE-DEP submenu → Taxi License (wpFetchCode = BS-NCA-ITL / taxiLicense). */
export function isItlTaxiDepToPaper(
    code?: string | null,
    wpFetchCode?: string | null,
): boolean {
    const upper = String(code || '').trim().toUpperCase();
    if (upper !== 'PL-AE-DEP') return false;
    const fetch = String(wpFetchCode || '').trim().toUpperCase();
    return fetch === 'BS-NCA-ITL' || fetch === 'TAXILICENSE' || fetch === 'TAXI_LICENSE';
}

/** BS-NCA-IA|ITL → nbvCfAdj；PL-AE-AIA → amorChgAdj；PL-AE-DEP only via Taxi submenu path */
export function resolveIaToPaperFieldKey(code?: string | null): 'nbvCfAdj' | 'amorChgAdj' {
    const upper = String(code || '').trim().toUpperCase();
    return upper === PL_AE_AIA_CODE || upper === 'PL-AE-DEP' ? 'amorChgAdj' : 'nbvCfAdj';
}

/** 浅拷贝 IA WP detail，避免污染 fsAdjustStore.wpDetailsCache */
function cloneIaDetailForToPaper(detail: any): any {
    if (!detail || typeof detail !== 'object') return {};
    const next: Record<string, any> = {...detail};
    const rows = next[IA_INTANGIBLE_ROWS_KEY];
    next[IA_INTANGIBLE_ROWS_KEY] = Array.isArray(rows)
        ? rows.map((row) => (row && typeof row === 'object' ? {...row} : row))
        : [];
    // CommonInvestment toPaper：保留 principalSubsidiaryRow（IA 无此键，拷贝无害）
    if (Array.isArray(next.principalSubsidiaryRow)) {
        next.principalSubsidiaryRow = next.principalSubsidiaryRow.map(
            (row: any) => (row && typeof row === 'object' ? {...row} : row),
        );
    }
    return next;
}

function isIaSelectableDataRow(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    return String(row.item || '').trim().length > 0;
}

function buildIaSelectionRows(detail: any): ToPaperWpRowSelectionRow[] {
    const rows = Array.isArray(detail?.[IA_INTANGIBLE_ROWS_KEY]) ? detail[IA_INTANGIBLE_ROWS_KEY] : [];
    const result: ToPaperWpRowSelectionRow[] = [];
    rows.forEach((row: any, rowIndex: number) => {
        if (!isIaSelectableDataRow(row)) return;
        result.push({
            arrayName: IA_INTANGIBLE_ROWS_KEY,
            rowIndex,
            rowKey: String(row.item || '').trim() || `Row ${rowIndex + 1}`,
            perClient: Number(row.nbvCf ?? row.costBf ?? row.perClient) || 0,
            row,
        });
    });
    return result;
}

/**
 * IA/AIA toPaper：在所选 intangibleRows 上 nest nbvCfAdj / amorChgAdj（不写 row.adjustment）。
 * 与 IntangibleFieldAdjustPanel / nestIntangibleFieldAdjOntoTargetRow 出站形状一致。
 */
export function applyIaToPaperRowDetail(
    detail: any,
    rowIndex: number,
    adjustmentAmount: number,
    code?: string | null,
): any {
    const fieldKey = resolveIaToPaperFieldKey(code);
    const base = cloneIaDetailForToPaper(detail);
    const existing = Array.isArray(base[IA_INTANGIBLE_ROWS_KEY]) ? base[IA_INTANGIBLE_ROWS_KEY] : [];
    const amount = Number(adjustmentAmount) || 0;
    base[IA_INTANGIBLE_ROWS_KEY] = existing.map((row: any, idx: number) => {
        if (!row || typeof row !== 'object') return row;
        const cleaned: any = {...row};
        delete cleaned.adjustments;
        delete cleaned.amorChgAdj;
        delete cleaned.nbvCfAdj;
        delete cleaned.adjustment;
        if (idx === rowIndex && Math.abs(amount) >= 0.001) {
            cleaned[fieldKey] = amount;
        }
        return cleaned;
    });
    delete base.amorChgAdj;
    delete base.nbvCfAdj;
    delete base.adjustment;
    delete base.intangibleRowId;
    return base;
}

/** 空 WP 专用：按 IntangibleRow 结构建空对象，写入 nbvCfAdj / amorChgAdj */
function createEmptyIaToPaperRow(
    adjustmentAmount: number,
    fieldKey: 'nbvCfAdj' | 'amorChgAdj',
): Record<string, any> {
    const row: Record<string, any> = {
        item: '',
        costBf: null,
        addition: null,
        costDisposal: null,
        accAmorBf: null,
        amorChg: null,
        accDisposal: null,
    };
    if (Math.abs(Number(adjustmentAmount) || 0) >= 0.001) {
        row[fieldKey] = adjustmentAmount;
    }
    return row;
}

/**
 * Intangible / Amortisation toPaper（直接叶子，无分类 wpFetchCode）：
 * - non-legacy: >=1 行 → rowSelection（末 Empty record）
 * - legacy: ≥2 行 → rowSelection；1 行 nest xxxAdj
 * - 0 行 → 空 IntangibleRow + xxxAdj → setDetail
 */
function resolveIaToPaperResult(
    input: {code: string; calcAdj: number},
    detail: any,
): ToPaperWpFetchResult {
    const {code, calcAdj} = input;
    const parDetail = cloneIaDetailForToPaper(detail);
    if (!Array.isArray(parDetail[IA_INTANGIBLE_ROWS_KEY])) {
        parDetail[IA_INTANGIBLE_ROWS_KEY] = [];
    }

    const selectionRows = buildIaSelectionRows(parDetail);

    if (shouldOpenToPaperRowSelection(code, selectionRows.length)) {
        return {
            kind: 'rowSelection',
            code,
            parDetail,
            adjustmentAmount: calcAdj,
            rows: withEmptyRecordOption(code, selectionRows, IA_INTANGIBLE_ROWS_KEY),
            waitForUser: true,
        };
    }

    if (selectionRows.length === 1) {
        const single = selectionRows[0];
        return {
            kind: 'setDetail',
            code,
            detail: applyIaToPaperRowDetail(parDetail, single.rowIndex, calcAdj, code),
            extraRowKeys: [`${code}:${IA_INTANGIBLE_ROWS_KEY}:${single.rowIndex}`],
        };
    }

    const fieldKey = resolveIaToPaperFieldKey(code);
    const emptyRow = createEmptyIaToPaperRow(calcAdj, fieldKey);
    return {
        kind: 'setDetail',
        code,
        detail: {[IA_INTANGIBLE_ROWS_KEY]: [emptyRow]},
        extraRowKeys: [`${code}:${IA_INTANGIBLE_ROWS_KEY}:0`],
    };
}

/**
 * IA/AIA/ITL / CommonInvestment toPaper field WP→WP。
 * Investment（BS-NCA-ISUB|IASS|BS-CA-IOT）与 IA 同形：nest nbvCfAdj 到 intangibleRows。
 * 仅由 adjustDraftStore.fetchAndAssembleToPaper / 选行回调显式调用；
 * 不接入 shared resolveToPaperWpFetchResult*，其它 toPaper 路径保持原逻辑。
 *
 * @param opts.detail - 已解包的 WP detail（空 WP 传 `{}`）
 */
export function tryBuildIaToPaperWpFetchResult(opts: {
    code: string;
    calcAdj: number;
    detail?: any;
    wpFetchCode?: string | null;
}): ToPaperWpFetchResult | null {
    const isItlDep = isItlTaxiDepToPaper(opts.code, opts.wpFetchCode);
    const isInv = isCommonInvestmentCode(String(opts.code || '').trim().toUpperCase());
    if (!isIaOrAiaToPaperCode(opts.code) && !isItlDep && !isInv) return null;
    const detail = opts.detail && typeof opts.detail === 'object' && !Array.isArray(opts.detail)
        ? opts.detail
        : {};
    return resolveIaToPaperResult(
        {code: opts.code, calcAdj: opts.calcAdj},
        detail,
    );
}

// ── Issued Capital toPaper（BS-EQ-IC）────────────────────────────────────
const ISSUED_CAPITAL_CODE = 'BS-EQ-IC';

/** BS-EQ-IC 作为 toPaper 叶子（其它 paper-registry 组件 → IC） */
export function isIssuedCapitalToPaperCode(code?: string | null): boolean {
    return String(code || '').trim().toUpperCase() === ISSUED_CAPITAL_CODE;
}

/** 浅拷贝 IC WP bag，避免污染 fsAdjustStore.wpDetailsCache */
function cloneIcDetailForToPaper(detail: any): Record<string, any> {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return {};
    const next: Record<string, any> = {...detail};
    for (const key of Object.keys(next)) {
        const value = next[key];
        if (Array.isArray(value)) {
            next[key] = value.map((row: any) => (
                row && typeof row === 'object' ? {...row} : row
            ));
        } else if (value && typeof value === 'object') {
            next[key] = {...value};
        }
    }
    // GET 附带的 schedule-level adjustments 列表仅展示用，组装 toPaper 时去掉
    delete next.adjustments;
    return next;
}

/**
 * Issued Capital toPaper（其它组件 → BS-EQ-IC）。
 * 拉取完整 WP bag 后仅写入顶层 `adjustment`，不改 shareholders/directors/…，不弹行选择。
 * 不接入 shared resolveToPaperWpFetchResult*（避免 scanAllRows 误绑股东行）。
 */
export function tryBuildIcToPaperWpFetchResult(opts: {
    code: string;
    calcAdj: number;
    detail?: any;
}): ToPaperWpFetchResult | null {
    if (!isIssuedCapitalToPaperCode(opts.code)) return null;
    const base = cloneIcDetailForToPaper(opts.detail);
    return {
        kind: 'setDetail',
        code: opts.code,
        detail: {
            ...base,
            adjustment: Number(opts.calcAdj) || 0,
        },
        extraRowKeys: [`${opts.code}:_synthetic:0`],
    };
}

// ── 工作底稿元数据判定（统一维护） ─────────────────────────────────────
/**
 * 仅包含附加元数据的 key（不属于工作底稿本体行数据）。
 * 后端 Facade 已分离返回；前端 mergeWorkingPaperQueryResponse 合并到首元素供组件读取。
 */
export const WP_METADATA_ONLY_KEYS = new Set([
    'SME-FRS',
    'WOKE_DONE',
    'glInvoiceDetails',
    'wpStatus',
    'wpIssueRemark',
    'autoAdjustments',
    // GET empty details=[] still returns these envelope flags; without them
    // isWpMetadataOnly stays false and CommonWp never runs initialDataFactory.
    'outstanding',
    'outstandingRemark',
    'issueStatus',
    'preparedBy',
    'reviewedBy',
    'preparedDate',
    'reviewedDate',
    'preparedUserIds',
    'reviewedUserIds',
]);

/**
 * 判断一个对象是否仅包含元数据 key（即没有实际工作底稿行数据）。
 * @param data - 后端返回的工作底稿数据对象（通常是 response[0]）
 * @returns true 表示数据为空或仅含 SME-FRS / WOKE_DONE / glInvoiceDetails
 */
export function isWpMetadataOnly(data: any): boolean {
    if (!data) return true;
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data !== 'object') return false;
    const keys = Object.keys(data);
    return keys.length === 0 || keys.every(k => WP_METADATA_ONLY_KEYS.has(k));
}

/**
 * Test / related / cutoff 等次要数组——有占位行也不算「主表有数据」。
 * MULTI 空 YOA Tab 种厂数据时忽略这些字段。
 */
const WP_SECONDARY_ARRAY_KEYS = new Set([
    'transactionTest',
    'cutOffThisYearTest',
    'cutOffNextYearTest',
    'relatedRows',
    'relatedItems',
    'testRow',
]);

/** 各 WP strategy 主行数组字段（有字段且全空 → 视为无主行） */
const WP_PRIMARY_ARRAY_KEYS = [
    'salesIncome',
    'purchases',
    'commonRow',
    'rows',
    'bankRow',
    'commissionPayments',
    'whtRows',
    'taxDeductible',
    'nonTaxDeductible',
    'bankInterestIncome',
    'otherInterestIncome',
    'gainExchange',
    'lossExchange',
    'gainDisposal',
    'lossDisposal',
    'dueFromToDirRow',
    'staffRow',
    'workerRow',
    'directorRow',
    'directorQuarterRow',
    'salary',
    'directorSalary',
    'quarter',
    'mpfSummary',
    'interestSummary',
    'depreciationSummary',
    'costRow',
    'impairmentLossRow',
    'principalSubsidiaryRow',
    'intangibleRows',
    'taxiLicenseRow',
    'fullPayTaxiCabRow',
    'hirePurchaseTaxiCabRow',
    'feesRow',
    'debtRow',
    'debtFeeRow',
    'inventoryRow',
    'bankLoans',
    'vehicleLoans',
    'shareholders',
    'locationOfPropertyRow',
    'commercialAllowanceRow',
    'industrialAllowanceRow',
    'furnitureFixtureRow',
    'computerHardSoftRow',
    'fullPayMotorRow',
    'hirePurchaseMotorRow',
    'machineryRow',
    'liftEscalatorRow',
    'equipmentRow',
] as const;

/**
 * MULTI Period Tab：判断 WP detail 是否缺少主表行（应走 initialDataFactory）。
 * - 忽略 SME-FRS / test / related 等次要字段
 * - 支持 details[0] 与 details[yoa]（YOA Map）嵌套
 */
export function isWpPrimaryRowsEmpty(
    data: any,
    yearOfAssessment?: string | null,
): boolean {
    if (!data) return true;
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data !== 'object') return false;
    if (isWpMetadataOnly(data)) return true;

    let bag: Record<string, any> = data;

    if (Array.isArray(data.details)
        && data.details[0]
        && typeof data.details[0] === 'object'
        && !Array.isArray(data.details[0])) {
        bag = data.details[0] as Record<string, any>;
    } else if (data.details && typeof data.details === 'object' && !Array.isArray(data.details)) {
        const yoa = String(yearOfAssessment || '').trim();
        const yoaKeys = Object.keys(data.details).filter((k) => /^\d{4}\/\d{2}$/.test(k));
        if (yoaKeys.length > 0) {
            if (yoa && data.details[yoa] != null) {
                return isWpPrimaryRowsEmpty(data.details[yoa], null);
            }
            return yoaKeys.every((k) => isWpPrimaryRowsEmpty(data.details[k], null));
        }
        bag = data.details as Record<string, any>;
    }

    const primaryPresent = WP_PRIMARY_ARRAY_KEYS.filter(
        (k) => Object.prototype.hasOwnProperty.call(bag, k) && Array.isArray(bag[k]),
    );
    if (primaryPresent.length > 0) {
        return primaryPresent.every((k) => !Array.isArray(bag[k]) || bag[k].length === 0);
    }

    const contentArrays = Object.entries(bag).filter(([k, v]) =>
        !WP_METADATA_ONLY_KEYS.has(k)
        && !WP_SECONDARY_ARRAY_KEYS.has(k)
        && k !== 'details'
        && Array.isArray(v),
    );
    if (contentArrays.length === 0) {
        return true;
    }
    return contentArrays.every(([, v]) => !(v as unknown[]).length);
}

/**
 * 仅把 factory 中非空主行数组填入 existing 的空主数组；保留 annualInfo 等原字段，不整包替换。
 */
export function seedWpEmptyPrimaryRowsFromFactory(existing: any, factoryData: any): any {
    if (!factoryData || typeof factoryData !== 'object' || Array.isArray(factoryData)) {
        return existing;
    }
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
        return factoryData;
    }
    const result: Record<string, any> = { ...existing };
    for (const key of WP_PRIMARY_ARRAY_KEYS) {
        const factoryRows = (factoryData as Record<string, any>)[key];
        if (!Array.isArray(factoryRows) || factoryRows.length === 0) continue;
        if (!Array.isArray(result[key]) || result[key].length === 0) {
            result[key] = factoryRows;
        }
    }
    return result;
}

/**
 * 从工作底稿数据中提取元数据部分（SME-FRS / WOKE_DONE / glInvoiceDetails），
 * 用于合并到 factory 默认数据中。
 * @param data - 后端返回的工作底稿数据对象
 * @returns 仅包含元数据 key 的对象，若无则返回空对象
 */
export function extractWpMetadata(data: any): Record<string, any> {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
    const meta: Record<string, any> = {};
    for (const k of WP_METADATA_ONLY_KEYS) {
        if (data[k] !== undefined) {
            meta[k] = data[k];
        }
    }
    return meta;
}

/**
 * Generate initial data for CommonInvestment working paper (BS-NCA-ISUB, BS-NCA-IASS, BS-CA-IOT).
 * Shape matches IntangibleSubTable / audit_row_investment ({@code intangibleRows}).
 * toPaper：adjustment 落 nbvCfAdj（IA parity），不再折入 costBf。
 */
export function createCommonInvestmentInitialData(
    code: string,
    lineAmount?: number | null,
    adjustmentAmount?: number
): { intangibleRows: any[]; principalSubsidiaryRow: any[] } {
    const itemName = code === 'BS-CA-IOT' ? 'Investment on trusts' : 'Unlisted shares';
    const adj = Number(adjustmentAmount) || 0;
    const costBf = lineAmount != null ? Math.abs(lineAmount) : null;
    const row: Record<string, any> = {
        id: -1,
        date: null,
        item: itemName,
        costBf,
        addition: null,
        costDisposal: null,
        accAmorBf: null,
        amorChg: null,
        otherValue: null,
        otherItem: null,
        accDisposal: null,
    };
    if (Math.abs(adj) >= 0.001) {
        row.nbvCfAdj = adj;
    }

    return {
        intangibleRows: [row],
        principalSubsidiaryRow: [],
    };
}

/**
 * Check if a code is a CommonInvestment type
 */
export function isCommonInvestmentCode(code: string): boolean {
    return ['BS-NCA-ISUB', 'BS-NCA-IASS', 'BS-CA-IOT'].includes(code);
}

/**
 * When intangibleRows missing, seed one IntangibleSubTable row. Keeps principalSubsidiaryRow.
 */
export function ensureCommonInvestmentTemplateRows(
    detail: any,
    code: string,
    lineAmount?: number | null,
    adjustmentAmount?: number,
): any {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return createCommonInvestmentInitialData(code, lineAmount, adjustmentAmount);
    }
    const rowsEmpty = !Array.isArray(detail.intangibleRows) || detail.intangibleRows.length === 0;
    if (!rowsEmpty) {
        return detail;
    }
    const factory = createCommonInvestmentInitialData(code, lineAmount, adjustmentAmount);
    return {
        ...detail,
        intangibleRows: factory.intangibleRows,
        principalSubsidiaryRow: Array.isArray(detail.principalSubsidiaryRow)
            ? detail.principalSubsidiaryRow
            : factory.principalSubsidiaryRow,
    };
}

/** Field keys on IntangibleRow that toPaper can target for CommonInvestment. */
export type CommonInvestmentFieldKey =
    | 'costBf'
    | 'addition'
    | 'costDisposal'
    | 'accAmorBf'
    | 'amorChg'
    | 'otherValue'
    | 'accDisposal';

/**
 * Field option for CommonInvestment toPaper selection (maps old vertical rows → columns).
 */
export interface CommonInvestmentRowOption {
    id: string;
    fieldKey: CommonInvestmentFieldKey;
    label: string;
    /** @deprecated kept for AdjustTabsDialog label binding */
    category?: 'cost' | 'impairment';
    /** @deprecated */
    rowIndex?: number;
}

/**
 * Get field options for CommonInvestment toPaper selection.
 */
export function getCommonInvestmentRowOptions(code: string): CommonInvestmentRowOption[] {
    const isInvestmentOnTrusts = code === 'BS-CA-IOT';
    if (isInvestmentOnTrusts) {
        return [
            { id: 'costBf', fieldKey: 'costBf', label: 'Cost B/F', category: 'cost', rowIndex: 0 },
            { id: 'addition', fieldKey: 'addition', label: 'Additions', category: 'cost', rowIndex: 1 },
            { id: 'costDisposal', fieldKey: 'costDisposal', label: 'Disposal (Cost)', category: 'cost', rowIndex: 2 },
            { id: 'accAmorBf', fieldKey: 'accAmorBf', label: 'Acc dep B/F', category: 'impairment', rowIndex: 0 },
            { id: 'amorChg', fieldKey: 'amorChg', label: 'Amor chg', category: 'impairment', rowIndex: 1 },
            { id: 'accDisposal', fieldKey: 'accDisposal', label: 'Disposal (Impairment)', category: 'impairment', rowIndex: 2 },
        ];
    }
    return [
        { id: 'costBf', fieldKey: 'costBf', label: 'Cost B/F', category: 'cost', rowIndex: 0 },
        { id: 'addition', fieldKey: 'addition', label: 'Additions', category: 'cost', rowIndex: 1 },
        { id: 'accAmorBf', fieldKey: 'accAmorBf', label: 'Acc dep B/F', category: 'impairment', rowIndex: 0 },
        { id: 'amorChg', fieldKey: 'amorChg', label: 'Amor chg', category: 'impairment', rowIndex: 1 },
        { id: 'otherValue', fieldKey: 'otherValue', label: 'other', category: 'impairment', rowIndex: 2 },
    ];
}

/**
 * Create CommonInvestment initial data with adjustment applied to a selected field.
 */
export function createCommonInvestmentInitialDataWithSelection(
    code: string,
    lineAmount: number | null | undefined,
    adjustmentAmount: number,
    selectedRowOption: CommonInvestmentRowOption
): { intangibleRows: any[]; principalSubsidiaryRow: any[] } {
    const baseData = createCommonInvestmentInitialData(code, lineAmount, 0);
    const row = baseData.intangibleRows[0];
    if (!row) return baseData;

    const field = selectedRowOption.fieldKey
        || (selectedRowOption.category === 'cost'
            ? (['costBf', 'addition', 'costDisposal'] as const)[selectedRowOption.rowIndex ?? 0]
            : (['accAmorBf', 'amorChg', 'otherValue'] as const)[selectedRowOption.rowIndex ?? 0]);

    const signed = (field === 'accAmorBf' || field === 'amorChg' || field === 'otherValue' || field === 'accDisposal')
        ? Math.abs(adjustmentAmount)
        : Math.abs(adjustmentAmount);

    baseData.intangibleRows[0] = {
        ...row,
        [field]: (Number(row[field as keyof typeof row]) || 0) + signed,
        ...(field === 'otherValue' && !row.otherItem
            ? {otherItem: 'Other issues (please name it)'}
            : {}),
    };
    return baseData;
}

/**
 * Empty GainLossExchange detail shell (row labels come from backend defaults).
 */
export function createEmptyGainLossExchangeDetail(): { gainExchange: CommonRow[]; lossExchange: CommonRow[] } {
    return {gainExchange: [], lossExchange: []};
}

/** Must stay aligned with GainLossExchangeWpEnum on the backend. */
export const GAIN_LOSS_EXCHANGE_GAIN_ROW_KEYS = [
    'Exchange Gain (taxable income)',
    'Exchange Gain (capital In nature)',
] as const;

export const GAIN_LOSS_EXCHANGE_LOSS_ROW_KEYS = [
    'Exchange Loss (tax deductible)',
    'Exchange Loss (capital In nature)',
] as const;

export type GainLossExchangeDetail = { gainExchange: CommonRow[]; lossExchange: CommonRow[] };

/** True when backend already returned a usable perClient (keep it; do not overwrite with parent FS). */
function hasGainLossExchangeBackendPerClient(row: CommonRow | undefined): boolean {
    if (!row) return false;
    const value = row.perClient;
    if (value === undefined || value === null) return false;
    // Default/placeholder rows often ship perClient=0 without a persisted id — treat as empty.
    const persisted = row.id != null && Number(row.id) > 0;
    if (!persisted && Number(value) === 0) return false;
    return true;
}

/** Apply FS per-client / last-year values to backend-provided rows by item label. */
export function applyGainLossExchangeFsAmounts(
    detail: GainLossExchangeDetail,
    options: {
        exgClientValue?: number;
        exlClientValue?: number;
        exgLastYearAudited?: number;
        exlLastYearAudited?: number;
        initialAdjustment?: number;
    },
): GainLossExchangeDetail {
    const gainExchange = (detail.gainExchange || []).map((row) => ({...row}));
    const lossExchange = (detail.lossExchange || []).map((row) => ({...row}));

    const patchByItem = (rows: CommonRow[], item: string, patch: Partial<CommonRow>) => {
        const idx = rows.findIndex((r) => r.item === item);
        if (idx >= 0) {
            rows[idx] = {...rows[idx], ...patch};
        }
    };

    const gainTaxableItem = GAIN_LOSS_EXCHANGE_GAIN_ROW_KEYS[0];
    const lossDeductibleItem = GAIN_LOSS_EXCHANGE_LOSS_ROW_KEYS[0];
    const gainTaxableRow = gainExchange.find((r) => r.item === gainTaxableItem);
    const lossDeductibleRow = lossExchange.find((r) => r.item === lossDeductibleItem);

    patchByItem(gainExchange, gainTaxableItem, {
        // Prefer backend perClient when present; otherwise init from parent Exchange Gain FS.
        ...(hasGainLossExchangeBackendPerClient(gainTaxableRow)
            ? {}
            : {perClient: options.exgClientValue ?? 0}),
        lastYearFinal: options.exgLastYearAudited ?? 0,
        ...(options.initialAdjustment !== undefined
            ? {adjustment: options.initialAdjustment}
            : {}),
    });
    patchByItem(lossExchange, lossDeductibleItem, {
        // Prefer backend perClient when present; otherwise init from parent Exchange Loss FS.
        ...(hasGainLossExchangeBackendPerClient(lossDeductibleRow)
            ? {}
            : {perClient: options.exlClientValue ?? 0}),
        lastYearFinal: options.exlLastYearAudited ?? 0,
    });

    return {gainExchange, lossExchange};
}

/**
 * Check if a code is a GainLossExchange type
 */
export function isGainLossExchangeCode(code: string): boolean {
    return ['PL-OI-EXG', 'PL-AE-EXL'].includes(code);
}

export function resolveGainLossExchangeSelectedSide(selectedFsCode: string): 'gain' | 'loss' {
    return String(selectedFsCode || '').trim().toUpperCase() === 'PL-AE-EXL' ? 'loss' : 'gain';
}

// ── WP_TO_WP toPaper item 自動複製工具 ──────────────────────────────────

/** 所有 Row 類型的標識字段名列表（按優先級排序） */
const IDENTITY_FIELD_NAMES = [
    'item',                    // CommonRow, PPERow
    'supplierName',            // CommonFeesRow
    'customer',                // CommonDebtRow
    'commissionPaymentName',   // CommissionPaymentRow
    'name',                    // PrincipalSubsidiaryRow, StaffSalaryRow
    'address',                 // RentalRow
] as const;

/** Row 數據的容器屬性名列表（按優先級排序） */
const ROW_CONTAINER_NAMES = [
    'commonRow', 'feesRow', 'debtRow', 'commissionPaymentRow',
    'principalSubsidiaryRow', 'salary', 'rentalRow', 'ppeRow',
] as const;

/**
 * 從 fromPaper detail 中提取第一行的標識字段值
 * 用於 WP_TO_WP 模式下 toPaper 的 initialDataFactory
 *
 * @param fallback 如果 fromPaper 無數據時的回退值
 * @returns 提取到的標識字段值，或 fallback
 */
export function extractFromPaperItemValue(fallback: string = ''): string {
    const fromPaper = useAdjustDraftStore.getState().fromPaper;
    const fromDetail = fromPaper?.adjustmentLine?.details;
    if (!fromDetail || typeof fromDetail !== 'object') return fallback;

    // 遍歷已知的行容器名，找到第一個非空數組
    let rows: any[] | null = null;
    for (const containerName of ROW_CONTAINER_NAMES) {
        const candidate = (fromDetail as any)[containerName];
        if (Array.isArray(candidate) && candidate.length > 0) {
            rows = candidate;
            break;
        }
    }
    if (!rows) return fallback;

    const firstRow = rows[0];
    // 遍歷標識字段名，找到第一個非空值
    for (const field of IDENTITY_FIELD_NAMES) {
        const val = firstRow[field];
        if (val && typeof val === 'string' && val.trim()) {
            return val.trim();
        }
    }
    return fallback;
}

// ── WP 响应解包 ─────────────────────────────────────────────────────────

/** 从 getWpDetail 返回的数组解包为 working paper detail 对象；无效时返回 null */
export function unwrapWorkingPaperDetail(wpData: any[] | null | undefined): any | null {
    if (!wpData || !Array.isArray(wpData) || wpData.length === 0) {
        return null;
    }

    let detail: any = wpData[0];

    if (detail && typeof detail === 'object' && detail.details != null) {
        detail = detail.details;
    }

    if (Array.isArray(detail)) {
        if (detail.length === 0) {
            detail = null;
        } else {
            detail = detail[0];
        }
    }

    if (detail && typeof detail === 'object' && detail.payload && typeof detail.payload === 'object') {
        detail = detail.payload;
    }

    if (!detail || typeof detail !== 'object') {
        return null;
    }

    return detail;
}

/** 计算 toPaper 初始化用的 perClient（auditedValue 优先，否则 openingAmount） */
export function computeToPaperPerClient(auditedValue: number, openingAmount?: number | null): number {
    return auditedValue !== 0 ? auditedValue : (openingAmount ?? 0);
}

/**
 * 移除 detail 中所有行数组内对象的 id 字段
 * 当后端 WP 查询为空时调用，避免提交时引用不存在的后端行 id
 */
export function stripIdsFromDetail(detail: any): any {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return detail;
    const result = {...detail};
    for (const key of Object.keys(result)) {
        if (Array.isArray(result[key])) {
            result[key] = result[key].map((row: any) => {
                if (row && typeof row === 'object' && 'id' in row) {
                    const {id, ...rest} = row;
                    return rest;
                }
                return row;
            });
        }
    }
    return result;
}

/** verify 路径：后端无 WP 数据时剥离 id，否则返回 null 表示无需更新 */
export function resolveVerifyToPaperDetail(existingDetail: any, wpData: any[] | null | undefined): any | null {
    if (wpData && Array.isArray(wpData) && wpData.length > 0) {
        return null;
    }
    if (!existingDetail) {
        return null;
    }
    return stripIdsFromDetail(existingDetail);
}

// ── 按 code 的初始 detail factory ───────────────────────────────────────

/**
 * 根据 code 创建初始 detail 数据结构，包含指定的调整金额
 * 用于 row 为空时，通过策略方法获取对应的接口数据结构
 */
export function createInitialDetailForCode(
    code: string,
    categoryLine: any,
    adjustmentAmount: number,
    options?: {wpFetchCode?: string | null; annualInfo?: any},
): any {
    const name = categoryLine?.aliasEn || categoryLine?.nameEn || code;
    const perClient = categoryLine?.amount || 0;

    const baseRow = {
        id: 1,
        item: name,
        itemEdit: true,
        perClient,
        adjustment: adjustmentAmount,
        finalAmount: perClient + adjustmentAmount,
        lastYearFinal: 0,
    };

    if (code === 'BS-CL-BOD' || code === 'BS-CA-CASH') {
        // WHY: 禁止写死 bankId:1（多笔 Empty/默认行会撞同一 attr.rowId 被合成一条占位）
        return {
            bankRow: [{
                bankId: -1,
                id: -1,
                bankName: '',
                accountNumber: '',
                perClient,
                currency: 'HKD',
                exchangeRate: 1,
                adjustment: adjustmentAmount,
                lastYearFinal: 0,
            }],
            commonRow: [],
        };
    }

    if (isCommonInvestmentCode(code)) {
        return createCommonInvestmentInitialData(code, perClient, adjustmentAmount);
    }

    if (isTurnoverIncomeFsCode(code)) {
        const detail: Record<string, any> = {
            salesIncome: [baseRow],
            transactionTest: [],
            cutOffThisYearTest: [],
            cutOffNextYearTest: [],
        };
        // 可选透传已有 annualInfo（由调用方提供）；不发明默认值，避免误写 account
        if (options?.annualInfo != null && typeof options.annualInfo === 'object') {
            detail.annualInfo = options.annualInfo;
        }
        return detail;
    }

    if (code === 'PL-COT-PUR') {
        return {purchases: [baseRow], transactionTest: [], cutOffThisYearTest: [], cutOffNextYearTest: []};
    }

    if (code === 'BS-CL-BLS' || code === 'BS-NCL-BLS') {
        return {bankLoans: [{id: 1, perClient, adjustment: adjustmentAmount, lastYearFinal: 0}]};
    }

    // PL-FC-LI adjustment execution: bankLoans|vehicleLoans + adjustment
    // (interestSummary is display-only in InterestSummaryWp)
    if (code === 'PL-FC-LI') {
        return createPlFcLiAdjustmentDetail(adjustmentAmount, {
            wpFetchCode: options?.wpFetchCode,
            perClient,
        });
    }

    if (code === 'BS-CL-OFL' || code === 'BS-NCL-OFL') {
        return {vehicleLoans: [{id: 1, perClient, adjustment: adjustmentAmount, lastYearFinal: 0}]};
    }

    // PL-AE-REN field WP→WP: rows + top-level adjustment
    if (code === 'PL-AE-REN') {
        return {
            rows: [{id: 1, item: name, perClient: 0, adjustment: 0, lastYearFinal: 0}],
            adjustment: adjustmentAmount,
        };
    }

    if (code === 'PL-TURN-RI') {
        return {
            rows: [],
            rentalOtherInfo: {},
            relatedItems: [],
            transactionTest: [],
            cutOffThisYearTest: [],
            cutOffNextYearTest: [],
        };
    }

    if (code === 'PL-OI-II' || code === 'PL-OI-SFG') {
        return {bankInterestIncome: [baseRow], otherInterestIncome: []};
    }

    if (code === 'PL-AE-DON') {
        return {taxDeductible: [baseRow], nonTaxDeductible: []};
    }

    if (code === 'PL-AE-COM') {
        return {commissionPayments: [baseRow]};
    }

    if (code === 'PL-AE-WHT') {
        return {
            whtRows: [{
                ...baseRow,
                incomeNature: name,
                jurisdiction: '',
                incomeAmount: null,
                foreignTaxRate: 0.1,
                withholdingTaxAmount: perClient,
                perClient,
                unilateralEligible: false,
                lastYearFinal: Number(baseRow.lastYearFinal) || 0,
            }],
            relatedItems: [],
        };
    }

    if (isGainLossExchangeCode(code)) {
        return createEmptyGainLossExchangeDetail();
    }

    if (code === 'PL-AE-SAL' || code === 'PL-AE-WAG') {
        return {
            salary: [{
                id: 1,
                rowKey: '',
                name,
                salary: perClient,
                adjustment: adjustmentAmount,
                lastYearFinal: 0,
                mpf: {
                    id: -1,
                    mpf: 0,
                    adjustment: 0,
                    lastYearFinal: 0,
                },
            }],
        };
    }

    // PL-AE-MPF：按二级菜单 wpFetchCode 组装 salary|wages|directorSalary + mpf.adjustment
    if (isPlAeMpfCode(code)) {
        return createPlAeMpfAdjustmentDetail(adjustmentAmount, {
            wpFetchCode: options?.wpFetchCode,
            perClient,
            name,
        });
    }

    if (code === 'PL-OI-GOD' || code === 'PL-AE-LOD') {
        return {
            locationOfPropertyDisRow: [baseRow],
            commercialAllowanceDisRow: [],
            industrialAllowanceDisRow: [],
            furnitureFixtureDisRow: [],
            computerHardSoftDisRow: [],
            fullPayMotorDisRow: [],
            hirePurchaseMotorDisRow: [],
            machineryDisRow: [],
            liftEscalatorDisRow: [],
            equipmentDisRow: [],
            subsidiaryRow: [],
            associateRow: [],
            intangibleRow: [],
            taxiLicenseDisRow: [],
            fullPayTaxiCabDisRow: [],
            hirePurchaseTaxiCabDisRow: [],
            trustsRow: [],
        };
    }

    if (code === 'BS-CA-DIR' || code === 'BS-CL-DTD') {
        return {
            dueFromToDirRow: [normalizeDueFromToDirRow({
                code,
                directorName: name,
                perClient,
                adjustment: adjustmentAmount,
                lastYearFinal: 0,
            })],
        };
    }

    const DEBT_FEES_CODES = ['PL-COT-SUB', 'PL-AE-BD', 'PL-AE-PDD', 'PL-AE-CF', 'PL-AE-LPF', 'PL-AE-MGT', 'PL-AE-SUE'];
    if (DEBT_FEES_CODES.includes(code.toUpperCase())) {
        const isDebt = code.toUpperCase() === 'PL-AE-BD' || code.toUpperCase() === 'PL-AE-PDD';
        return {
            debtFeeRow: [{
                id: 0,
                date: null,
                invoiceNo: '',
                name,
                description: isDebt ? null : '',
                perClient,
                adjustment: adjustmentAmount,
                lastYearFinal: 0,
            }],
            relatedRows: [],
        };
    }

    // DividendWp / flat equity：表单存正数派息总额；commonRow.perClient 取反（由 WP UI 派生）
    if (code === 'BS-EQ-DIV') {
        const totalDividendAmount = Math.abs(Number(perClient) || 0);
        return {
            dividendPaid: 0,
            dividendCurrency: 'HKD',
            amountPerEachShare: 0,
            totalDividendAmount,
            adjustment: adjustmentAmount,
        };
    }

    if (code === 'BS-EQ-AL') {
        return {
            perClient,
            adjustment: adjustmentAmount,
        };
    }

    return {commonRow: [baseRow]};
}

/** 根据 code 确定主数组名（用于添加新行时确定正确的数组） */
export function getMainArrayNameForCode(code: string, options?: {wpFetchCode?: string | null}): string {
    if (code === 'BS-CL-BOD' || code === 'BS-CA-CASH') return 'bankRow';
    if (['BS-NCA-ISUB', 'BS-NCA-IASS', 'BS-CA-IOT'].includes(code)) return 'intangibleRows';
    if (isTurnoverIncomeFsCode(code)) return 'salesIncome';
    if (code === 'PL-COT-PUR') return 'purchases';
    if (code === 'BS-CL-BLS' || code === 'BS-NCL-BLS') return 'bankLoans';
    // PL-FC-LI / PL-AE-REN AdjustTabs edits top-level adjustment via `_synthetic`
    if (code === 'PL-FC-LI' || code === 'PL-AE-REN' || code === 'PL-TURN-RI') return '_synthetic';
    if (code === 'BS-CL-OFL' || code === 'BS-NCL-OFL') return 'vehicleLoans';
    if (code === 'PL-OI-II' || code === 'PL-OI-SFG') return 'bankInterestIncome';
    if (code === 'PL-AE-DON') return 'taxDeductible';
    if (code === 'PL-AE-COM') return 'commissionPayments';
    if (code === 'PL-AE-WHT') return 'whtRows';
    if (code === 'PL-AE-SAL' || code === 'PL-AE-WAG') return 'salary';
    if (isPlAeMpfCode(code)) return resolvePlAeMpfDetailArrayName(options?.wpFetchCode);
    if (code === 'PL-OI-EXG' || code === 'PL-AE-EXL') return 'gainExchange';
    if (code === 'PL-OI-GOD' || code === 'PL-AE-LOD') return 'locationOfPropertyDisRow';
    if (code === 'BS-CA-DIR' || code === 'BS-CL-DTD') return 'dueFromToDirRow';
    // ITL only — same array key as IA; IA/AIA keep dedicated tryBuildIa* path (unchanged)
    if (String(code || '').trim().toUpperCase() === 'BS-NCA-ITL') return 'hirePurchaseTaxiCabRow';
    if (isPpeOrDepToPaperCode(code)) {
        const category = resolvePpeCategoryFetchCode(options?.wpFetchCode);
        if (category) return category;
    }
    if (['PL-COT-SUB', 'PL-AE-BD', 'PL-AE-PDD', 'PL-AE-CF', 'PL-AE-LPF', 'PL-AE-MGT', 'PL-AE-SUE'].includes(code.toUpperCase())) {
        return 'debtFeeRow';
    }
    return 'commonRow';
}

/** 检查 code 是否需要用户选择主数组 */
export function getArrayOptionsForCode(code: string): Array<{ key: string; label: string }> | null {
    if (code === 'BS-CA-CASH') {
        return [{key: 'bankRow', label: 'Bank Accounts'}, {key: 'commonRow', label: 'Other Items'}];
    }
    if (code === 'PL-OI-II' || code === 'PL-OI-SFG') {
        return [{key: 'bankInterestIncome', label: 'Bank Interest Income'}, {
            key: 'otherInterestIncome',
            label: 'Other Interest Income',
        }];
    }
    if (code === 'PL-AE-DON') {
        return [{key: 'taxDeductible', label: 'Tax Deductible'}, {
            key: 'nonTaxDeductible',
            label: 'Non-Tax Deductible',
        }];
    }
    if (code === 'PL-OI-EXG' || code === 'PL-AE-EXL') {
        return [{key: 'gainExchange', label: 'Gain on Exchange'}, {key: 'lossExchange', label: 'Loss on Exchange'}];
    }
    return null;
}

/** 从工作底稿 detail 移除 adjustments 数组并将 adjustment 写入首行 */
export function prepareBackendDetail(detail: any, adjustmentAmount: number): any {
    if (!detail || typeof detail !== 'object') return detail;
    const result: any = {};
    let firstRowSet = false;

    for (const [key, value] of Object.entries(detail)) {
        if (WP_METADATA_ONLY_KEYS.has(key)) {
            result[key] = value;
            continue;
        }
        if (!Array.isArray(value)) {
            result[key] = value;
            continue;
        }
        result[key] = value.map((row: any) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned: any = {...row};
            delete cleaned.adjustments;
            delete cleaned.mpfAdjustments;
            if (!firstRowSet) {
                cleaned.adjustment = adjustmentAmount;
                if ('mpfAdjustment' in row) cleaned.mpfAdjustment = 0;
                firstRowSet = true;
            } else {
                cleaned.adjustment = 0;
                if ('mpfAdjustment' in row) cleaned.mpfAdjustment = 0;
            }
            return cleaned;
        });
    }
    return result;
}

/** 更新指定行的 adjustment 值，其他行清零 */
export function updateRowAdjustment(
    detail: any,
    targetArrayName: string,
    targetRowIndex: number,
    adjustmentAmount: number,
): any {
    if (!detail || typeof detail !== 'object') return detail;
    const result: any = {};

    for (const [key, value] of Object.entries(detail)) {
        if (WP_METADATA_ONLY_KEYS.has(key)) {
            result[key] = value;
            continue;
        }
        if (!Array.isArray(value)) {
            result[key] = value;
            continue;
        }
        result[key] = value.map((row: any, idx: number) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned: any = {...row};
            delete cleaned.adjustments;
            delete cleaned.mpfAdjustments;
            if (key === targetArrayName && idx === targetRowIndex) {
                cleaned.adjustment = adjustmentAmount;
            } else {
                cleaned.adjustment = 0;
            }
            if ('mpfAdjustment' in row) cleaned.mpfAdjustment = 0;
            return cleaned;
        });
    }
    return result;
}

/** 写入 adjustment；Director 节点额外按 toPaper.code 绑定 dueFromToDirRow[].code；仅 PL-AE-MPF 写 mpf.adjustment */
export function applyToPaperRowDetail(
    detail: any,
    toPaperCode: string,
    arrayName: string,
    rowIndex: number,
    adjustmentAmount: number,
    options?: {selectedRow?: ToPaperWpRowSelectionRow},
): any {
    if (isDueToDirectorsCode(toPaperCode) && arrayName === 'dueFromToDirRow') {
        return buildDueFromToDirDetailForToPaper(detail, toPaperCode, adjustmentAmount, {
            targetRowIndex: rowIndex,
        });
    }
    // 仅 PL-AE-MPF：adjustment 落在 mpf.adjustment（不影响 SAL/WAG/DR 等写 row.adjustment）
    if (isPlAeMpfCode(toPaperCode)) {
        return updateMpfRowAdjustment(detail, arrayName, rowIndex, adjustmentAmount);
    }
    // PL-AE-REN 二级菜单：按选中的 month / other expense 写入 field maps
    if (isPlAeRenCode(toPaperCode) && options?.selectedRow) {
        return applyPlAeRenRowSelection(detail, options.selectedRow, adjustmentAmount);
    }
    return updateRowAdjustment(detail, arrayName, rowIndex, adjustmentAmount);
}

// ── ToPaper WP fetch 纯解析（AdjustTabsDialog 855–1222 迁移） ───────────

export type ToPaperArrayOption = { key: string; label: string };

export type ToPaperWpRowSelectionRow = {
    arrayName: string;
    rowIndex: number;
    rowKey: string;
    perClient: number;
    row: any;
    /** Nested adjustment path, e.g. 'mpf' for row.mpf.adjustment (PL-AE-MPF) */
    subField?: string;
};

/**
 * Preset-schedule FS codes: keep legacy rowSelection only when existing rows > 1
 * (no Empty record option). From TaxWp / GainLoss / DP / IML preset analysis.
 */
export const ROW_SELECTION_LEGACY_THRESHOLD_CODES = new Set<string>([
    'BS-CA-TAXR',
    'BS-CL-TAXP',
    'PL-TAX',
    'PL-OI-EXG',
    'PL-AE-EXL',
    'BS-CL-DP',
    'PL-AE-IML',
]);

export const EMPTY_RECORD_ROW_KEY = 'Empty record';
export const EMPTY_RECORD_ROW_INDEX = -1;

export function isRowSelectionLegacyThresholdCode(code?: string | null): boolean {
    return ROW_SELECTION_LEGACY_THRESHOLD_CODES.has(String(code || '').trim().toUpperCase());
}

/** Non-legacy toPaper nodes: offer Empty record when backend has >= 1 row. */
export function shouldOfferEmptyRecordRowSelection(code?: string | null): boolean {
    return !isRowSelectionLegacyThresholdCode(code);
}

export function isEmptyRecordSelectionRow(row?: ToPaperWpRowSelectionRow | null): boolean {
    if (!row) return false;
    return row.rowIndex === EMPTY_RECORD_ROW_INDEX
        || String(row.rowKey || '').trim() === EMPTY_RECORD_ROW_KEY;
}

export function buildEmptyRecordSelectionRow(
    arrayName: string,
    subField?: string,
): ToPaperWpRowSelectionRow {
    return {
        arrayName,
        rowIndex: EMPTY_RECORD_ROW_INDEX,
        rowKey: EMPTY_RECORD_ROW_KEY,
        perClient: 0,
        row: null,
        ...(subField ? {subField} : {}),
    };
}

/** Legacy: open only when > 1. Others: open when >= 1. */
export function shouldOpenToPaperRowSelection(code: string, existingRowCount: number): boolean {
    if (existingRowCount <= 0) return false;
    return isRowSelectionLegacyThresholdCode(code)
        ? existingRowCount > 1
        : existingRowCount >= 1;
}

/** Append Empty record last for non-legacy codes when there is at least one real row. */
export function withEmptyRecordOption(
    code: string,
    rows: ToPaperWpRowSelectionRow[],
    arrayName?: string,
): ToPaperWpRowSelectionRow[] {
    const realRows = rows.filter((r) => !isEmptyRecordSelectionRow(r));
    if (!shouldOfferEmptyRecordRowSelection(code) || realRows.length === 0) return rows;
    const arr = arrayName || realRows[0]?.arrayName || getMainArrayNameForCode(code);
    const subField = realRows[0]?.subField;
    return [...realRows, buildEmptyRecordSelectionRow(arr, subField)];
}

/**
 * User picked Empty record: append a blank row and bind adjustmentAmount
 * so WP dynamic render feeds back into the new empty row.
 */
export function applyEmptyRecordToPaperDetail(opts: {
    code: string;
    parDetail: any;
    adjustmentAmount: number;
    arrayName?: string;
    subField?: string;
    wpFetchCode?: string | null;
}): {detail: any; extraRowKeys: string[]} {
    const code = String(opts.code || '').trim().toUpperCase();
    const adj = Number(opts.adjustmentAmount) || 0;
    const wpFetchCode = opts.wpFetchCode;

    if (isPlAeRenCode(code)) {
        const sub = String(opts.subField || '').trim();
        if (sub === 'otherExpense' || String(opts.arrayName || '') === 'extraExpenseRows') {
            const created = applyPlAeRenNewOtherExpense(opts.parDetail, adj);
            return {detail: created.detail, extraRowKeys: [created.extraRowKey]};
        }
        const next = cloneRentalDetailForToPaper(opts.parDetail || {});
        next.adjustment = adj;
        next.rentalAdjustment = adj;
        next.otherExpenseAdjustment = 0;
        next.rentalMonthAdjustments = {};
        next.otherExpenseAdjustmentsById = {};
        return {detail: next, extraRowKeys: [`${code}:_synthetic:0`]};
    }

    if (isPlAeMpfCode(code)) {
        const arrayName = (opts.arrayName as PlAeMpfDetailArrayName)
            || resolvePlAeMpfDetailArrayName(wpFetchCode);
        const base = opts.parDetail && typeof opts.parDetail === 'object'
            ? {...opts.parDetail}
            : {};
        const existing = Array.isArray(base[arrayName]) ? base[arrayName] : [];
        const cleared = existing.map((row: any) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned: any = {...row, adjustment: 0};
            if (cleaned.mpf && typeof cleaned.mpf === 'object') {
                cleaned.mpf = {...cleaned.mpf, adjustment: 0};
            }
            return cleaned;
        });
        const mpf = {id: -1, mpf: 0, adjustment: adj, lastYearFinal: 0};
        if (arrayName === 'directorSalary') {
            cleared.push({
                id: 0,
                directorId: null,
                name: '',
                salary: 0,
                adjustment: 0,
                lastYearFinal: 0,
                mpf,
            });
        } else {
            cleared.push({
                id: 0,
                rowKey: '',
                name: '',
                salary: 0,
                adjustment: 0,
                lastYearFinal: 0,
                mpf,
            });
        }
        base[arrayName] = cleared;
        return {
            detail: base,
            extraRowKeys: [`${code}:${arrayName}:${cleared.length - 1}:mpf`],
        };
    }

    const ppeCategory = resolvePpeCategoryFetchCode(wpFetchCode)
        || (isPpeCategoryFetchCode(opts.arrayName) ? String(opts.arrayName) : null);
    if (isPpeOrDepToPaperCode(code) && ppeCategory) {
        const arrayName = ppeCategory;
        const base = clonePpeDetailForToPaper(opts.parDetail);
        const existing = Array.isArray(base[arrayName]) ? base[arrayName] : [];
        const cleared = existing.map((row: any) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned: any = {...row};
            delete cleaned.adjustments;
            cleaned.adjustment = 0;
            return cleaned;
        });
        cleared.push({
            date: null,
            item: '',
            costBf: null,
            additions: null,
            disposalCost: null,
            accDepBf: null,
            adjustment: adj,
        });
        base[arrayName] = cleared;
        return {
            detail: base,
            extraRowKeys: [`${code}:${arrayName}:${cleared.length - 1}`],
        };
    }

    if (
        isIaOrAiaToPaperCode(code)
        || isItlTaxiDepToPaper(code, wpFetchCode)
        || isCommonInvestmentCode(code)
    ) {
        const fieldKey = resolveIaToPaperFieldKey(code);
        const base = cloneIaDetailForToPaper(opts.parDetail);
        const existing = Array.isArray(base[IA_INTANGIBLE_ROWS_KEY])
            ? base[IA_INTANGIBLE_ROWS_KEY]
            : [];
        const cleared = existing.map((row: any) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned: any = {...row};
            delete cleaned.adjustments;
            delete cleaned.amorChgAdj;
            delete cleaned.nbvCfAdj;
            delete cleaned.adjustment;
            return cleaned;
        });
        cleared.push(createEmptyIaToPaperRow(adj, fieldKey));
        base[IA_INTANGIBLE_ROWS_KEY] = cleared;
        delete base.amorChgAdj;
        delete base.nbvCfAdj;
        delete base.adjustment;
        delete base.intangibleRowId;
        return {
            detail: base,
            extraRowKeys: [`${code}:${IA_INTANGIBLE_ROWS_KEY}:${cleared.length - 1}`],
        };
    }

    const arrayName = opts.arrayName || getMainArrayNameForCode(code, {wpFetchCode});
    const base = opts.parDetail && typeof opts.parDetail === 'object' ? {...opts.parDetail} : {};
    if (arrayName === '_synthetic') {
        const initialDetail = createInitialDetailForCode(code, {}, adj, {wpFetchCode});
        return {
            detail: initialDetail,
            extraRowKeys: [`${code}:${getMainArrayNameForCode(code, {wpFetchCode})}:0`],
        };
    }
    const existing = Array.isArray(base[arrayName]) ? base[arrayName] : [];
    const cleared = existing.map((row: any) => {
        if (!row || typeof row !== 'object') return row;
        const cleaned: any = {...row};
        delete cleaned.adjustments;
        cleaned.adjustment = 0;
        return cleaned;
    });
    // bankRow Empty record: bank-shaped shell + unique negative id (independent from other empty shells)
    if (arrayName === 'bankRow') {
        const usedNeg = cleared
            .map((r: any) => Number(r?.bankId ?? r?.id) || 0)
            .filter((n: number) => n < 0);
        const nextNeg = usedNeg.length > 0 ? Math.min(...usedNeg) - 1 : -1;
        cleared.push({
            bankId: nextNeg,
            id: nextNeg,
            bankName: '',
            accountNumber: '',
            perClient: 0,
            adjustment: adj,
            lastYearFinal: 0,
        });
    } else {
        cleared.push({
            id: 0,
            item: '',
            itemEdit: true,
            perClient: 0,
            adjustment: adj,
            lastYearFinal: 0,
        });
    }
    base[arrayName] = cleared;
    return {
        detail: base,
        extraRowKeys: [`${code}:${arrayName}:${cleared.length - 1}`],
    };
}

/** RowSelector options for Gain/Loss Exchange toPaper, filtered by FsSelectionDrawer node. */
export function filterGainLossExchangeRowSelectionRows(
    rows: ToPaperWpRowSelectionRow[],
    selectedFsCode: string,
): ToPaperWpRowSelectionRow[] {
    const side = resolveGainLossExchangeSelectedSide(selectedFsCode);
    const targetArray = side === 'loss' ? 'lossExchange' : 'gainExchange';
    const allowedKeys: readonly string[] = side === 'loss'
        ? GAIN_LOSS_EXCHANGE_LOSS_ROW_KEYS
        : GAIN_LOSS_EXCHANGE_GAIN_ROW_KEYS;

    const byRowKey = rows.filter((r) => allowedKeys.includes(r.rowKey));
    if (byRowKey.length > 0) return byRowKey;

    return rows.filter((r) => r.arrayName === targetArray);
}

export {
    filterTaxWpRowSelectionRows,
    isTaxWpFsCode,
    resolveTaxWpToPaperDefaultLogicalIndex,
    resolveTaxWpToPaperSelectableLogicalIndices,
} from '@/wp/tax/taxWpFromPaper';

export type ToPaperWpFetchResult =
    | { kind: 'none' }
    | { kind: 'commonInvestmentSelection'; code: string; adjustmentAmount: number; perClient: number }
    | {
        kind: 'arraySelection';
        code: string;
        codeName: string;
        parDetail: any | null;
        adjustmentAmount: number;
        perClient: number;
        options: ToPaperArrayOption[];
    }
    | { kind: 'setDetail'; code: string; detail: any; extraRowKeys?: string[] }
    | {
        kind: 'setFlatEquityDetail';
        code: string;
        detail: any;
        adjustment: number;
        extraRowKeys: string[];
    }
    | {
        kind: 'rowSelection';
        code: string;
        parDetail: any;
        adjustmentAmount: number;
        rows: ToPaperWpRowSelectionRow[];
        waitForUser: true;
    }
    | { kind: 'noDirectorConfirm'; code: string }
    | {
        kind: 'mpfSourceEmptyConfirm';
        code: string;
        message: string;
        redirectCode: string;
    };

export interface ResolveToPaperWpFetchInput {
    code: string;
    wpData: any[] | null | undefined;
    calcAdj: number;
    toLine: { name?: string; scheduleName?: string; openingAmount?: number } | null;
    perClient: number;
    /** PL-FC-LI interest / PL-AE-MPF / PL-AE-REN / PPE 分类 二级菜单 discriminator 或实际拉取 code */
    wpFetchCode?: string;
    /** PL-AE-REN month 展开用年度期间 */
    periodFrom?: string | null;
    periodTo?: string | null;
}

const getToPaperLineName = (toLine: ResolveToPaperWpFetchInput['toLine'], code: string): string =>
    toLine?.name || toLine?.scheduleName || code;

/**
 * DIR/DTD toPaper rowSelection：展示底稿全部董事行（不按余额本侧过滤）。
 * 选 BS-CA-DIR 或 BS-CL-DTD 均可选任一侧有余额的董事。
 */
const filterDirectorRowsForAdjustment = (rows: AdjustedRowInfo[], fsCode: string): AdjustedRowInfo[] => {
    const normalizedCode = String(fsCode || '').trim().toUpperCase();
    if (!isDueToDirectorsCode(normalizedCode)) return rows;
    return rows.filter((r) => isDueFromToDirDirectorRow(r.row ?? {}));
};

/**
 * BS-EQ-DIV toPaper：按 CommonWp commonRow 整理 detail（保留 dividend 表单字段供 WP 展示）。
 */
export function buildDividendToPaperCommonRowDetail(input: {
    calcAdj: number;
    perClient?: number;
    item?: string;
    lastYearFinal?: number;
    source?: Record<string, any> | null;
}): Record<string, any> {
    const source = input.source && typeof input.source === 'object' ? input.source : {};
    const totalDividendAmount = Math.abs(Number(
        source.totalDividendAmount ?? input.perClient ?? 0,
    ) || 0);
    // Per client = negated total money amount of dividend paid
    const perClient = -totalDividendAmount;
    const item = String(
        input.item
        || source.commonRow?.[0]?.item
        || 'Total Dividend Amount',
    ).trim() || 'Total Dividend Amount';

    const row: Record<string, any> = {
        id: Number(source.commonRow?.[0]?.id) || 0,
        item,
        perClient,
        adjustment: input.calcAdj,
        lastYearFinal: Number(
            source.commonRow?.[0]?.lastYearFinal
            ?? input.lastYearFinal
            ?? 0,
        ) || 0,
        itemEdit: false,
    };

    return {
        dividendPaid: source.dividendPaid ?? 0,
        dividendCurrency: source.dividendCurrency || 'HKD',
        amountPerEachShare: Number(source.amountPerEachShare ?? 0) || 0,
        totalDividendAmount,
        commonRow: [row],
    };
}

function resolveToPaperEmptyOrUnwrappedFallback(
    input: ResolveToPaperWpFetchInput,
    parDetail: any | null,
    metadata?: Record<string, any>,
): ToPaperWpFetchResult {
    const {code, calcAdj, toLine, perClient} = input;
    const toLineName = getToPaperLineName(toLine, code);

    if (isDueToDirectorsCode(code)) {
        return {kind: 'noDirectorConfirm', code};
    }

    // PL-AE-MPF + 二级菜单 wpFetchCode：源 WP 为空时确认跳转；否则保持原 fallback
    if (isPlAeMpfCode(code)) {
        const emptyConfirm = buildPlAeMpfSourceEmptyConfirmResult(code, input.wpFetchCode);
        if (emptyConfirm) return emptyConfirm;
    }

    // PL-AE-REN 二级菜单：openingAmount==0 → WP→FS 空 detail；否则空源确认 / 新建
    if (isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(input.wpFetchCode)) {
        return resolvePlAeRenToPaperResult(input, parDetail || {});
    }

    // PPE / Depreciation 分类：按 wpFetchCode（xxxRow）绑定
    if (isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(input.wpFetchCode)) {
        return resolvePpeToPaperResult(input, parDetail || {});
    }

    // CommonInvestment toPaper：空 WP → IA-style nest nbvCfAdj（不弹字段选择）
    if (isCommonInvestmentCode(code)) {
        return tryBuildIaToPaperWpFetchResult({
            code,
            calcAdj,
            detail: parDetail || {},
        }) ?? {kind: 'none'};
    }

    // 仅 toPaper 空数据：DIV 整理为 commonRow；不改 createInitialDetailForCode 的 flat 默认
    if (String(code || '').trim().toUpperCase() === 'BS-EQ-DIV') {
        const detail = buildDividendToPaperCommonRowDetail({
            calcAdj,
            perClient,
            item: toLineName || 'Total Dividend Amount',
            lastYearFinal: Number((toLine as any)?.lastYearAudited ?? 0) || 0,
            source: parDetail,
        });
        if (metadata && detail && typeof detail === 'object') {
            Object.assign(detail, metadata);
        }
        return {
            kind: 'setDetail',
            code,
            detail,
            extraRowKeys: [`${code}:commonRow:0`],
        };
    }

    const arrayOptions = getArrayOptionsForCode(code);
    if (arrayOptions) {
        return {
            kind: 'arraySelection',
            code,
            codeName: toLineName,
            parDetail,
            adjustmentAmount: calcAdj,
            perClient,
            options: arrayOptions,
        };
    }

    const initialDetail = createInitialDetailForCode(
        code,
        {...toLine, amount: perClient},
        calcAdj,
        {wpFetchCode: input.wpFetchCode},
    );
    if (metadata && initialDetail && typeof initialDetail === 'object' && !Array.isArray(initialDetail)) {
        Object.assign(initialDetail, metadata);
    }
    const arrayName = getMainArrayNameForCode(code, {wpFetchCode: input.wpFetchCode});
    return {
        kind: 'setDetail',
        code,
        detail: initialDetail,
        extraRowKeys: [`${code}:${arrayName}:0`],
    };
}

function resolveToPaperWithDetail(
    input: ResolveToPaperWpFetchInput,
    detail: any,
): ToPaperWpFetchResult {
    const {code, calcAdj, toLine, perClient} = input;
    const toLineName = getToPaperLineName(toLine, code);
    const normalizedDetail = isDueToDirectorsCode(code)
        ? normalizeDueFromToDirDetail(detail)
        : detail;

    if (isDueToDirectorsCode(code) && dueFromToDirDetailRequiresIssuedCapital(normalizedDetail)) {
        return {kind: 'noDirectorConfirm', code};
    }

    if (isWpMetadataOnly(normalizedDetail)) {
        const metadata = extractWpMetadata(normalizedDetail);
        // CommonInvestment：与空 WP 相同，由 IA-style nbvCfAdj 装配接管（勿弹字段选择）
        return resolveToPaperEmptyOrUnwrappedFallback(input, null, metadata);
    }

    // BS-EQ-DIV toPaper：按 CommonWp commonRow 整理（不再走 flat equity _synthetic）
    if (String(code || '').trim().toUpperCase() === 'BS-EQ-DIV') {
        const detail = buildDividendToPaperCommonRowDetail({
            calcAdj,
            perClient,
            item: toLineName || 'Total Dividend Amount',
            lastYearFinal: Number((toLine as any)?.lastYearAudited ?? 0) || 0,
            source: normalizedDetail,
        });
        return {
            kind: 'setDetail',
            code,
            detail,
            extraRowKeys: [`${code}:commonRow:0`],
        };
    }

    // CommonInvestment toPaper：与 IA 相同 nest nbvCfAdj（勿走 scanAllRows → row.adjustment）
    if (isCommonInvestmentCode(code)) {
        return tryBuildIaToPaperWpFetchResult({
            code,
            calcAdj,
            detail: normalizedDetail,
        }) ?? {kind: 'none'};
    }

    if (isFlatEquityAdjustmentCode(code)) {
        return {
            kind: 'setFlatEquityDetail',
            code,
            detail: {
                perClient: Number(normalizedDetail?.perClient) || perClient,
                adjustment: calcAdj,
            },
            adjustment: calcAdj,
            extraRowKeys: [`${code}:_synthetic:0`],
        };
    }

    // PL-FC-LI：adjustment 执行态统一为 bankLoans|vehicleLoans + adjustment
    // interestSummary 仅为 InterestSummaryWp 展示；WP→WP 须带 loan 数组以落 attr → inAdj
    const codeUpper = String(code || '').trim().toUpperCase();
    if (codeUpper === 'PL-FC-LI') {
        return {
            kind: 'setDetail',
            code,
            detail: ensurePlFcLiLoanFieldAdjustmentDetail(normalizedDetail, calcAdj, {
                wpFetchCode: input.wpFetchCode,
                perClient,
            }),
            extraRowKeys: [`${code}:_synthetic:0`],
        };
    }

    // PL-AE-MPF：按 wpFetchCode 组装 salary|wages|directorSalary，adjustment 落在 mpf.adjustment
    if (isPlAeMpfCode(codeUpper)) {
        return resolvePlAeMpfToPaperResult(input, normalizedDetail);
    }

    // PL-AE-REN：二级菜单 rental/other（openingAmount / 空源确认 / 月或 other 选行）
    if (isPlAeRenCode(codeUpper) && isPlAeRenSubmenuFetchCode(input.wpFetchCode)) {
        return resolvePlAeRenToPaperResult(input, normalizedDetail);
    }

    // PPE / Depreciation：按分类 xxxRow 选行或直绑
    if (isPpeOrDepToPaperCode(codeUpper) && isPpeCategoryFetchCode(input.wpFetchCode)) {
        return resolvePpeToPaperResult(input, normalizedDetail);
    }

    const rawRows = scanAllRows(normalizedDetail);
    let eligibleRows = filterDirectorRowsForAdjustment(rawRows, code);

    // BankLoan / VehicleLoan 等主行无 adjustment|perClient|item|salary|name，
    // scanAllRows 会跳过 → 旧逻辑走 prepareBackendDetail 静默绑第一行，跳过 Empty record。
    // 主数组兜底：与 CommonWp 一样，>=1 行即弹 RowSelection（末 Empty record）。
    if (eligibleRows.length === 0) {
        const mainArrayName = getMainArrayNameForCode(code, {wpFetchCode: input.wpFetchCode});
        if (mainArrayName && mainArrayName !== '_synthetic') {
            const mainArr = Array.isArray((normalizedDetail as any)?.[mainArrayName])
                ? ((normalizedDetail as any)[mainArrayName] as any[])
                : [];
            eligibleRows = mainArr
                .map((row, rowIndex): AdjustedRowInfo | null => {
                    if (!row || typeof row !== 'object') return null;
                    return {
                        arrayName: mainArrayName,
                        rowIndex,
                        row,
                        adjustment: Number(row.adjustment) || 0,
                        rowKey: mainArrayName === 'bankRow'
                            ? formatBankCashRowSelectionLabel(row, rowIndex)
                            : String(
                                row.item
                                || row.directorName
                                || row.name
                                || row.bank
                                || row.loanNo
                                || row.mv
                                || row.hpComp
                                || row.bankName
                                || `Row ${rowIndex + 1}`,
                            ),
                        perClient: Number(row.perClient || row.salary) || 0,
                    };
                })
                .filter((r): r is AdjustedRowInfo => r != null);
            eligibleRows = filterDirectorRowsForAdjustment(eligibleRows, code);
        }
    }

    if (shouldOpenToPaperRowSelection(code, eligibleRows.length)) {
        const mapped = eligibleRows.map((r) => ({
            arrayName: r.arrayName,
            rowIndex: r.rowIndex,
            rowKey: r.arrayName === 'bankRow'
                ? formatBankCashRowSelectionLabel(r.row, r.rowIndex)
                : r.rowKey,
            perClient: r.perClient,
            row: r.row,
        }));
        return {
            kind: 'rowSelection',
            code,
            parDetail: normalizedDetail,
            adjustmentAmount: calcAdj,
            rows: withEmptyRecordOption(code, mapped),
            waitForUser: true,
        };
    }

    if (eligibleRows.length === 1) {
        const singleRow = eligibleRows[0];
        const updatedDetail = applyToPaperRowDetail(
            normalizedDetail,
            code,
            singleRow.arrayName,
            singleRow.rowIndex,
            calcAdj,
        );
        return {
            kind: 'setDetail',
            code,
            detail: updatedDetail,
            extraRowKeys: [`${code}:${singleRow.arrayName}:${singleRow.rowIndex}`],
        };
    }

    if (isDueToDirectorsCode(code) && rawRows.length > 0) {
        // 无董事行可弹选时仍写入 calcAdj；保留董事行自身 perClient，勿用 FS 节点额覆盖
        const boundDetail = buildDueFromToDirDetailForToPaper(normalizedDetail, code, calcAdj);
        const arrayName = getMainArrayNameForCode(code);
        const boundRows = boundDetail?.dueFromToDirRow ?? [];
        const targetIndex = boundRows.findIndex(
            (r: any) => String(r?.code || '').trim().toUpperCase() === String(code).trim().toUpperCase()
                && Math.abs(Number(r?.adjustment) || 0) >= 0.001,
        );
        return {
            kind: 'setDetail',
            code,
            detail: boundDetail,
            extraRowKeys: [`${code}:${arrayName}:${targetIndex >= 0 ? targetIndex : 0}`],
        };
    }

    const arrayOptions = getArrayOptionsForCode(code);
    if (arrayOptions) {
        return {
            kind: 'arraySelection',
            code,
            codeName: toLineName,
            parDetail: normalizedDetail,
            adjustmentAmount: calcAdj,
            perClient: 0,
            options: arrayOptions,
        };
    }

    // Empty main row array (e.g. CommonWp `commonRow: []`): invent one row like SINGLE empty-WP path.
    // prepareBackendDetail alone cannot write calcAdj onto zero-length arrays → submit details null / amount 0.
    const mainArrayName = getMainArrayNameForCode(code, {wpFetchCode: input.wpFetchCode});
    const mainRows = Array.isArray((normalizedDetail as any)?.[mainArrayName])
        ? (normalizedDetail as any)[mainArrayName] as any[]
        : null;
    if (
        mainArrayName !== '_synthetic'
        && (mainRows == null || mainRows.length === 0)
        && Math.abs(Number(calcAdj) || 0) >= 0.001
    ) {
        const initialDetail = createInitialDetailForCode(
            code,
            {...toLine, amount: perClient},
            calcAdj,
            {wpFetchCode: input.wpFetchCode},
        );
        if (initialDetail && typeof initialDetail === 'object' && !Array.isArray(initialDetail)) {
            for (const [key, value] of Object.entries(normalizedDetail || {})) {
                if (WP_METADATA_ONLY_KEYS.has(key) && !(key in initialDetail)) {
                    (initialDetail as any)[key] = value;
                }
            }
        }
        return {
            kind: 'setDetail',
            code,
            detail: initialDetail,
            extraRowKeys: [`${code}:${mainArrayName}:0`],
        };
    }

    const processed = prepareBackendDetail(normalizedDetail, calcAdj);
    const finalDetail = isDueToDirectorsCode(code)
        ? buildDueFromToDirDetailForToPaper(processed, code, calcAdj)
        : processed;
    return {kind: 'setDetail', code, detail: finalDetail};
}

/** 解析 toPaper WP fetch 结果（对应 AdjustTabsDialog 855–1182 try 分支） */
export function resolveToPaperWpFetchResult(input: ResolveToPaperWpFetchInput): ToPaperWpFetchResult {
    const {wpData, code} = input;
    if (!wpData || !Array.isArray(wpData) || wpData.length === 0) {
        if (isPlAeMpfCode(code)) {
            const emptyConfirm = buildPlAeMpfSourceEmptyConfirmResult(code, input.wpFetchCode);
            if (emptyConfirm) return emptyConfirm;
        }
        if (isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(input.wpFetchCode)) {
            return resolvePlAeRenToPaperResult(input, {});
        }
        if (isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(input.wpFetchCode)) {
            return resolvePpeToPaperResult(input, {});
        }
        // Align with catch / unwrap-null path: invent CommonWp-style detail (do not leave toPaper empty)
        return resolveToPaperEmptyOrUnwrappedFallback(input, null);
    }

    const detail = unwrapWorkingPaperDetail(wpData);
    if (!detail) {
        return resolveToPaperEmptyOrUnwrappedFallback(input, null);
    }
    return resolveToPaperWithDetail(input, detail);
}

/** 解析 toPaper WP fetch 失败回退（对应 AdjustTabsDialog catch 分支） */
export function resolveToPaperWpFetchErrorResult(input: ResolveToPaperWpFetchInput): ToPaperWpFetchResult {
    const {code, calcAdj, toLine, perClient} = input;
    const toLineName = getToPaperLineName(toLine, code);

    if (isDueToDirectorsCode(code)) {
        return {kind: 'noDirectorConfirm', code};
    }

    if (isPlAeMpfCode(code)) {
        const emptyConfirm = buildPlAeMpfSourceEmptyConfirmResult(code, input.wpFetchCode);
        if (emptyConfirm) return emptyConfirm;
    }

    if (isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(input.wpFetchCode)) {
        return resolvePlAeRenToPaperResult(input, {});
    }

    if (isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(input.wpFetchCode)) {
        return resolvePpeToPaperResult(input, {});
    }

    const arrayOptions = getArrayOptionsForCode(code);

    if (arrayOptions) {
        return {
            kind: 'arraySelection',
            code,
            codeName: toLineName,
            parDetail: null,
            adjustmentAmount: calcAdj,
            perClient,
            options: arrayOptions,
        };
    }

    const initialDetail = createInitialDetailForCode(
        code,
        {...toLine, amount: perClient},
        calcAdj,
        {wpFetchCode: input.wpFetchCode},
    );
    const arrayName = getMainArrayNameForCode(code, {wpFetchCode: input.wpFetchCode});
    return {
        kind: 'setDetail',
        code,
        detail: initialDetail,
        extraRowKeys: [`${code}:${arrayName}:0`],
    };
}
